import { useMemo, useState, useRef, useEffect, useCallback, useLayoutEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import {
    Search, RotateCcw, GitCompare, ChevronRight, ChevronUp, ImageOff, Settings2,
    ChevronLeft, ChevronRight as ChevronRightIcon, ChevronsLeft, ChevronsRight,
    CheckSquare, Square, Sparkles, FilterX, Ruler, X, Columns3, Eye, EyeOff, GripVertical, XCircle, ArrowRight, Tags, Scale,
    FileSpreadsheet, Upload, Loader2, CheckCircle2, AlertCircle, FileDown, FileUp, Flag, CalendarDays, Save,
} from 'lucide-react';
import {
    DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors,
    type DragEndEvent,
} from '@dnd-kit/core';
import {
    SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { useDataStore, FIRST_IMAGE_FIELD } from '@/store/useDataStore';
import { parseImageUrls, importExcel, exportExcel, buildSkuName, joinImageUrls } from '@/services/excelService';
import { useConfigStore } from '@/store/useConfigStore';
import { markBatchProductCompleted, setBatchLifecycleHandler, shouldSkipBatchProduct, useBatchStore } from '@/store/useBatchStore';
import { selectOCRInstance } from '@/services/ocrService';
import { generateImageWithFallback, getSelectedImageEditInstances, proxyUrl } from '@/services/imageEditService';
import { uploadImageToStorage } from '@/services/storageService';
import { compressImageInBrowser, compressionMaxBytes } from '@/services/imageCompressionService';
import { Button } from '@/components/ui/Button';
import { Input, Textarea } from '@/components/ui/Input';
import { Modal, ConfirmDialog } from '@/components/ui/Modal';
import { CompareModal, ProductCompareModal } from '@/components/CompareModal';
import { CellEditModal } from '@/components/CellEditModal';
import { StepConfirmModal } from '@/components/StepConfirmModal';
import { ImageEditorModal } from '@/components/ImageEditorModal';
import { cn } from '@/lib/utils';
import { DISPLAY_PRIORITY_FIELDS, SKU_SPECIFIC_FIELDS, EXCEL_FIELDS, FLAG_OPTIONS, DEFAULT_FLAG } from '@/lib/constants';
import type { DataRow, ExcelField, ImageEditInstance, ExcelData, FlagColor, LowQualityFilterConfig } from '@/types';
import { checkpointServerBrowserTask, deleteServerBrowserTask, loadCollectionBox, loadServerBrowserTask, saveCollectionBox, useSourceStore, type CollectionBoxFilters } from '@/store/useSourceStore';
import {
    cleanupCollectionBoxTaskDrafts,
    completedTaskKey,
    deleteCollectionBoxTaskDraft,
    isCollectionBoxTaskAbandoned,
    loadRecoverableCollectionBoxTask,
    markCollectionBoxTaskAbandoned,
    saveCollectionBoxTaskDraft,
    type CollectionBoxTaskDraft,
    type PersistedBatchTaskType,
} from '@/services/collectionBoxTaskPersistence';

const SKU_PAGE_SIZE = 20;
interface Group {
    productId: string;
    rows: { row: DataRow; index: number }[];
}

interface SearchCriteria {
    search: string;
    createdFrom: string;
    createdTo: string;
    processedFrom: string;
    processedTo: string;
    flagFilter: FlagColor | null;
}

interface CellEditState {
    rowIndex: number;
    field: ExcelField;
    currentValue: string;
    originalValue: string;
    rowNumber: number;
}

interface BatchState {
    field: ExcelField;
    mode: 'replace' | 'set';
    selectedRowIndices: number[];
}

interface TaskChipProps {
    active: boolean;
    disabled: boolean;
    icon: ReactNode;
    label: string;
    title?: string;
    onClick: () => void;
}

function TaskChip({ active, disabled, icon, label, title, onClick }: TaskChipProps) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            title={title}
            className={cn(
                'flex items-center gap-1 rounded-md px-3 py-1.5 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40',
                active
                    ? 'bg-[#159b96] text-white shadow-sm'
                    : 'bg-white text-[#159b96] ring-1 ring-inset ring-[#8dd4d1] hover:bg-[#edf9f8]',
            )}
        >
            {icon}
            {label}
        </button>
    );
}

function CollectionFilterGroup({ label, children }: { label?: string; children: ReactNode }) {
    return (
        <div className="flex h-8 min-w-0 items-center overflow-hidden rounded border border-[#d1d5db] bg-white">
            {label && <span className="shrink-0 pl-2 text-xs text-zinc-500">{label}</span>}
            {children}
        </div>
    );
}

export function DataTablePage() {
    const { fields, rows, originalRows, updateCell, resetToOriginal, cellErrors, filteredRowIndices, unmarkRowsFiltered, importData, recordImageReplacement, imageReplacements, productFlags, setProductFlag, description, fileName } = useDataStore();
    const {
        titleConfig, lowQualityConfig, sizeChartConfig, skuConfig,
        ocrInstances, llmInstances,
        imageEditInstances, imageEditSettings,
        hiddenFields, toggleFieldVisibility, setAllFieldsVisible,
        fieldOrder, setFieldOrder, resetFieldOrder,
        stepConfirmEnabled, toggleStepConfirm,
        stepBatchSize, setStepBatchSize,
    } = useConfigStore();
    const { tasks, runBatch, runStepBatch, stepState } = useBatchStore();
    const { mode: sourceMode, collectionStatus, sourcePlatforms, groupOptions, pagination: sourcePagination, loading: sourceLoading, saving: sourceSaving, saveResult, saveError, setSaving: setSourceSaving, setSaveResult, setSaveError } = useSourceStore();

    const [search, setSearch] = useState('');
    const [createdFrom, setCreatedFrom] = useState('');
    const [createdTo, setCreatedTo] = useState('');
    const [processedFrom, setProcessedFrom] = useState('');
    const [processedTo, setProcessedTo] = useState('');
    const [appliedSearch, setAppliedSearch] = useState<SearchCriteria>({ search: '', createdFrom: '', createdTo: '', processedFrom: '', processedTo: '', flagFilter: null });
    // 旗子筛选：null 表示不过滤
    const [flagFilter, setFlagFilter] = useState<FlagColor | null>(null);
    const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
    const [groupPages, setGroupPages] = useState<Record<string, number>>({});
    const [cellEdit, setCellEdit] = useState<CellEditState | null>(null);
    const [compareOpen, setCompareOpen] = useState(false);
    const [compareProductId, setCompareProductId] = useState<string | null>(null);
    const [batch, setBatch] = useState<BatchState | null>(null);
    const [findValue, setFindValue] = useState('');
    const [replaceValue, setReplaceValue] = useState('');
    const [setValue, setSetValue] = useState('');
    const [hoverImage, setHoverImage] = useState<{ url: string; originalUrl?: string; left: number; top: number } | null>(null);
    const [colVisOpen, setColVisOpen] = useState(false);
    const [collectionFiltersCollapsed, setCollectionFiltersCollapsed] = useState(false);
    const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
    const [collectionFilters, setCollectionFilters] = useState<CollectionBoxFilters>({
        keyword: '',
        source_item_id: '',
        source_platform: '',
        price_min: '',
        price_max: '',
        weight_min: '',
        weight_max: '',
        group_id: '',
        notes: '',
        is_claimed_shopee: '',
        claim_platform: '',
        time_field: 'created_at',
        time_start: '',
        time_end: '',
    });

    // 图片编辑器状态
    const [editorOpen, setEditorOpen] = useState(false);
    const [editorImageUrl, setEditorImageUrl] = useState('');
    const [editorReadOnly, setEditorReadOnly] = useState(false);
    const editorOnSaveRef = useRef<((url: string) => void) | null>(null);

    const [selectedProducts, setSelectedProducts] = useState<Set<string>>(new Set());
    const [recoveryDraft, setRecoveryDraft] = useState<CollectionBoxTaskDraft | null>(null);
    const [browserTaskRunning, setBrowserTaskRunning] = useState(false);
    const activeDraftRef = useRef<CollectionBoxTaskDraft | null>(null);
    const checkpointCountRef = useRef(0);
    const [groupPage, setGroupPage] = useState(1);
    const [groupPageSize, setGroupPageSize] = useState(10);
    const [jumpPage, setJumpPage] = useState('');

    // AI 编辑首图状态
    const [aiEditRunning, setAiEditRunning] = useState(false);
    const hasActiveAiTask = useMemo(
        () => browserTaskRunning || stepState.active || Array.from(tasks.values()).some(task => task.running),
        [browserTaskRunning, stepState.active, tasks],
    );

    useEffect(() => {
        if (sourceMode !== 'collection-box' || window.parent === window) return undefined;
        const parentOrigin = new URLSearchParams(window.location.search).get('parent_origin');
        if (!parentOrigin) return undefined;

        window.parent.postMessage({
            source: 'tabbygrid',
            type: 'collection-box:task-state',
            running: hasActiveAiTask,
        }, parentOrigin);

        return () => {
            window.parent.postMessage({
                source: 'tabbygrid',
                type: 'collection-box:task-state',
                running: false,
            }, parentOrigin);
        };
    }, [hasActiveAiTask, sourceMode]);
    const [aiEditProgress, setAiEditProgress] = useState('');

    // 空状态导入
    const emptyFileInputRef = useRef<HTMLInputElement>(null);
    // 选中操作栏导入
    const toolbarFileInputRef = useRef<HTMLInputElement>(null);
    const [importing, setImporting] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    const [importError, setImportError] = useState('');

    const handleFileImport = useCallback(async (file: File) => {
        if (!file) return;
        setImporting(true);
        setImportError('');
        try {
            const data = await importExcel(file);
            importData(data);
        } catch (err) {
            setImportError(err instanceof Error ? err.message : '导入失败');
        } finally {
            setImporting(false);
            if (emptyFileInputRef.current) emptyFileInputRef.current.value = '';
        }
    }, [importData]);

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setDragOver(false);
        const file = e.dataTransfer.files?.[0];
        if (file && /\.(xlsx|xls)$/i.test(file.name)) {
            handleFileImport(file);
        }
    }, [handleFileImport]);

    // 前端显示字段（考虑隐藏 + 自定义顺序）
    const displayFields = useMemo(() => {
        const allFields = [FIRST_IMAGE_FIELD, ...fields];
        // 过滤掉隐藏字段
        const visible = allFields.filter(f => !hiddenFields.includes(f.key));
        // 如果有自定义顺序，按顺序排列；无则按默认优先级
        if (fieldOrder.length > 0) {
            const ordered = fieldOrder
                .map(key => visible.find(f => f.key === key))
                .filter((f): f is ExcelField => !!f);
            // 追加未在 order 中的可见字段
            const rest = visible.filter(f => !fieldOrder.includes(f.key));
            return [...ordered, ...rest];
        }
        const priority = DISPLAY_PRIORITY_FIELDS
            .map(key => visible.find(f => f.key === key))
            .filter((f): f is ExcelField => !!f);
        const rest = visible.filter(f => !DISPLAY_PRIORITY_FIELDS.includes(f.key));
        return [...priority, ...rest];
    }, [fields, hiddenFields, fieldOrder]);

    const commonDisplayFields = useMemo(() => displayFields.filter(f => !SKU_SPECIFIC_FIELDS.has(f.key)), [displayFields]);
    const skuDisplayFields = useMemo(() => displayFields.filter(f => SKU_SPECIFIC_FIELDS.has(f.key)), [displayFields]);

    const groups = useMemo(() => {
        const q = appliedSearch.search.trim().toLowerCase();
        const fq = appliedSearch.flagFilter;
        const map = new Map<string, Group>();
        rows.forEach((row, index) => {
            const pid = (row.productId ?? '').trim() || `__noid_${index}__`;
            if (q) {
                const name = (row.productName ?? '').toLowerCase();
                const id = (row.productId ?? '').toLowerCase();
                if (!name.includes(q) && !id.includes(q)) return;
            }
            const createdDate = String(row.createdAt ?? '').slice(0, 10);
            if (appliedSearch.createdFrom && (!createdDate || createdDate < appliedSearch.createdFrom)) return;
            if (appliedSearch.createdTo && (!createdDate || createdDate > appliedSearch.createdTo)) return;
            const processedDate = String(row.processedAt ?? '').slice(0, 10);
            if (appliedSearch.processedFrom && (!processedDate || processedDate < appliedSearch.processedFrom)) return;
            if (appliedSearch.processedTo && (!processedDate || processedDate > appliedSearch.processedTo)) return;
            // 旗子维度筛选：未显式设置旗子的产品视为默认灰色
            if (fq) {
                if ((productFlags[pid] ?? DEFAULT_FLAG) !== fq) return;
            }
            if (!map.has(pid)) map.set(pid, { productId: pid, rows: [] });
            map.get(pid)!.rows.push({ row, index });
        });
        return Array.from(map.values());
    }, [rows, appliedSearch, productFlags]);

    // 全局产品分页
    const totalGroupPages = sourceMode === 'collection-box'
        ? Math.max(1, sourcePagination.last_page)
        : Math.max(1, Math.ceil(groups.length / groupPageSize));
    const safeGroupPage = sourceMode === 'collection-box'
        ? Math.min(sourcePagination.current_page, totalGroupPages)
        : Math.min(groupPage, totalGroupPages);
    const pagedGroups = sourceMode === 'collection-box'
        ? groups
        : groups.slice((safeGroupPage - 1) * groupPageSize, safeGroupPage * groupPageSize);

    const selectedRowIndices = useMemo(() => {
        if (selectedProducts.size === 0) return [];
        const indices: number[] = [];
        groups.forEach(g => { if (selectedProducts.has(g.productId)) g.rows.forEach(r => indices.push(r.index)); });
        return indices;
    }, [selectedProducts, groups]);

    const toggleGroup = (productId: string) => {
        setExpandedGroups(prev => { const n = new Set(prev); n.has(productId) ? n.delete(productId) : n.add(productId); return n; });
    };

    // 搜索变化时重置分页
    useEffect(() => { setGroupPage(1); }, [appliedSearch]);

    const setGroupSkuPage = (productId: string, page: number) => setGroupPages(prev => ({ ...prev, [productId]: page }));
    const toggleProductSelect = (productId: string) => setSelectedProducts(prev => { const n = new Set(prev); n.has(productId) ? n.delete(productId) : n.add(productId); return n; });
    const selectAll = () => setSelectedProducts(new Set(groups.map(g => g.productId)));
    const deselectAll = () => setSelectedProducts(new Set());
    const allProductsSelected = groups.length > 0 && groups.every(g => selectedProducts.has(g.productId));

    const openCellEdit = (rowIndex: number, field: ExcelField) => {
        const currentValue = rows[rowIndex]?.[field.key] ?? '';
        const originalValue = originalRows[rowIndex]?.[field.key] ?? '';
        setCellEdit({ rowIndex, field, currentValue, originalValue, rowNumber: rowIndex + 1 });
    };
    const handleCellSave = (value: string) => { if (cellEdit) updateCell(cellEdit.rowIndex, cellEdit.field.key, value); };

    const openBatch = (field: ExcelField) => {
        const indices = selectedProducts.size > 0 ? selectedRowIndices : rows.map((_, i) => i);
        setBatch({ field, mode: 'replace', selectedRowIndices: indices });
        setFindValue(''); setReplaceValue(''); setSetValue('');
    };
    const applyBatch = () => {
        if (!batch) return;
        const indices = batch.selectedRowIndices;
        if (batch.mode === 'replace' && findValue) {
            indices.forEach(i => { const v = rows[i]?.[batch.field.key] ?? ''; if (v.includes(findValue)) updateCell(i, batch.field.key, v.split(findValue).join(replaceValue)); });
        } else if (batch.mode === 'set') {
            indices.forEach(i => updateCell(i, batch.field.key, setValue));
        }
        setBatch(null);
    };

    // 导出：有选中则导出选中产品的所有行；未选中则导出全部（均排除被过滤标记的行）
    const handleExportSelected = () => {
        const exportRows = rows.filter((row, i) => {
            if (filteredRowIndices.has(i)) return false;
            if (selectedProducts.size === 0) return true;
            const pid = (row.productId ?? '').trim() || `__noid_${i}__`;
            return selectedProducts.has(pid);
        });
        if (exportRows.length === 0) return;
        const data: ExcelData = { description, headers: fields, rows: exportRows, fileName: fileName || 'export.xlsx' };
        const base = fileName?.replace(/\.(xlsx|xls)$/, '') || 'selected';
        const suffix = selectedProducts.size > 0 ? `_选中${exportRows.length}行` : `_全部${exportRows.length}行`;
        exportExcel(data, `${base}${suffix}.xlsx`, { overwritePlatformSpec: skuConfig.overwritePlatformSpec });
    };

    const performCollectionBoxSave = async () => {
        if (sourceSaving) return;
        setSaveConfirmOpen(false);
        if (selectedRowIndices.length === 0) {
            setSaveResult(null);
            setSaveError('尚未勾选商品');
            return;
        }

        const selectedIndexSet = new Set(selectedRowIndices);
        const selectedRows: DataRow[] = [];
        const selectedFilteredRowIndices = new Set<number>();
        rows.forEach((row, rowIndex) => {
            if (!selectedIndexSet.has(rowIndex)) return;
            const selectedRowIndex = selectedRows.length;
            selectedRows.push(row);
            if (filteredRowIndices.has(rowIndex)) selectedFilteredRowIndices.add(selectedRowIndex);
        });

        setSourceSaving(true);
        setSaveResult(null);
        setSaveError('');
        try {
            const result = await saveCollectionBox(selectedRows, selectedFilteredRowIndices);
            setSaveResult(result);
            if (result.success_count > 0 && activeDraftRef.current) {
                const successfulItemIds = new Set(result.items.map(item => String(item.item_id)));
                const savedProductIds = new Set(selectedRows
                    .filter(row => successfulItemIds.has(String(row.__commonItemId ?? '')))
                    .map((row, index) => (row.productId ?? '').trim() || `__noid_${index}__`));
                const draft = activeDraftRef.current;
                draft.selectedProductIds = draft.selectedProductIds.filter(productId => !savedProductIds.has(productId));
                draft.completedProductTasks = draft.completedProductTasks.filter(key => !Array.from(savedProductIds).some(productId => key.endsWith(`:${productId}`)));
                if (draft.selectedProductIds.length === 0) {
                    await deleteCollectionBoxTaskDraft(draft.taskId);
                    try { await deleteServerBrowserTask(); } catch (_) { /* 正式保存结果不受草稿清理异常影响 */ }
                    activeDraftRef.current = null;
                } else {
                    await persistActiveDraft('awaiting_confirmation', true);
                }
            }
            if (collectionStatus.startsWith('unprocessed_') && result.success_count > 0) {
                const remainingTotal = Math.max(0, sourcePagination.total - result.success_count);
                const lastPage = Math.max(1, Math.ceil(remainingTotal / sourcePagination.per_page));
                loadCollectionBox(collectionFilters, Math.min(sourcePagination.current_page, lastPage), sourcePagination.per_page);
            }
        } catch (error) {
            setSaveError(error instanceof Error ? error.message : '公共采集箱保存失败');
        } finally {
            setSourceSaving(false);
        }
    };

    const handleCollectionBoxSave = () => {
        if (selectedRowIndices.length === 0) {
            setSaveResult(null);
            setSaveError('尚未勾选商品');
            return;
        }
        const selectedIndexSet = new Set(selectedRowIndices);
        const selectedFilteredCount = Array.from(filteredRowIndices).filter(index => selectedIndexSet.has(index)).length;
        if (selectedFilteredCount > 0) setSaveConfirmOpen(true);
        else void performCollectionBoxSave();
    };

    // 构建反向映射：newUrl → oldUrl，用于 hover 时查找原图
    const reverseImageMap = useMemo(() => {
        const m = new Map<string, string>();
        for (const [oldUrl, newUrl] of imageReplacements) {
            m.set(newUrl, oldUrl);
        }
        return m;
    }, [imageReplacements]);

    const handleThumbEnter = (e: React.MouseEvent, url: string) => {
        const originalUrl = reverseImageMap.get(url);
        setHoverImage({ url, originalUrl, left: Math.min(e.clientX + 16, window.innerWidth - (originalUrl ? 500 : 220)), top: Math.min(e.clientY + 16, window.innerHeight - 220) });
    };

    const handleImageClick = (url: string, onSave: (newUrl: string) => void) => {
        setEditorImageUrl(url);
        setEditorReadOnly(false);
        editorOnSaveRef.current = (newUrl: string) => {
            if (newUrl && newUrl !== url) recordImageReplacement(url, newUrl);
            onSave(newUrl);
        };
        setEditorOpen(true);
    };

    const [selectedTasks, setSelectedTasks] = useState<Set<'title' | 'filter' | 'size' | 'aiImage' | 'sku-spec' | 'sku-weight'>>(new Set());

    const persistActiveDraft = useCallback(async (status: CollectionBoxTaskDraft['status'], syncServer = false) => {
        const current = activeDraftRef.current;
        if (!current) return;
        const data = useDataStore.getState();
        const now = Date.now();
        const next: CollectionBoxTaskDraft = {
            ...current,
            status,
            unsavedChanges: status !== 'completed',
            rows: data.rows,
            originalRows: data.originalRows,
            fields: data.fields,
            description: data.description,
            fileName: data.fileName,
            cellErrors: data.cellErrors,
            filteredRowIndices: Array.from(data.filteredRowIndices),
            imageReplacements: Array.from(data.imageReplacements),
            productFlags: data.productFlags,
            updatedAt: now,
            estimatedBytes: 0,
        };
        try {
            activeDraftRef.current = await saveCollectionBoxTaskDraft(next);
        } catch (error) {
            setSaveError('浏览器本地任务草稿写入失败，AI 任务已暂停。请检查浏览器存储空间后重试。');
            throw error;
        }
        if (syncServer) {
            try { await checkpointServerBrowserTask(activeDraftRef.current); }
            catch (error) { console.warn('[AI任务草稿] 服务器检查点暂存失败，继续使用 IndexedDB：', error); }
        }
    }, [setSaveError]);

    useEffect(() => {
        if (sourceMode !== 'collection-box') return undefined;
        setBatchLifecycleHandler({
            shouldSkip: (taskType, productId) => {
                const draft = activeDraftRef.current;
                return draft ? draft.completedProductTasks.includes(completedTaskKey(taskType, productId)) : false;
            },
            onProductCompleted: async (taskType, productId) => {
                const draft = activeDraftRef.current;
                if (!draft) return;
                const key = completedTaskKey(taskType, productId);
                if (!draft.completedProductTasks.includes(key)) draft.completedProductTasks.push(key);
                const total = Math.max(1, draft.selectedProductIds.length * draft.taskTypes.length);
                draft.progress = {
                    completed: draft.completedProductTasks.length,
                    total,
                    message: `已完成 ${draft.completedProductTasks.length}/${total} 个商品任务`,
                };
                checkpointCountRef.current++;
                await persistActiveDraft('running', checkpointCountRef.current % 5 === 0);
            },
        });
        return () => setBatchLifecycleHandler(null);
    }, [persistActiveDraft, sourceMode]);

    useEffect(() => {
        if (sourceMode !== 'collection-box') return undefined;
        const flushBeforeLeaving = () => {
            const draft = activeDraftRef.current;
            if (!draft || draft.status !== 'running') return;
            draft.updatedAt = Date.now();
            void checkpointServerBrowserTask(draft, true).catch(() => undefined);
        };
        window.addEventListener('pagehide', flushBeforeLeaving);
        document.addEventListener('visibilitychange', flushBeforeLeaving);
        return () => {
            window.removeEventListener('pagehide', flushBeforeLeaving);
            document.removeEventListener('visibilitychange', flushBeforeLeaving);
        };
    }, [sourceMode]);

    useEffect(() => {
        if (sourceMode !== 'collection-box') return undefined;
        let cancelled = false;
        const restore = async () => {
            let local: CollectionBoxTaskDraft | null = null;
            try {
                await cleanupCollectionBoxTaskDrafts();
                local = await loadRecoverableCollectionBoxTask(collectionStatus);
            } catch (error) {
                console.warn('[AI任务草稿] IndexedDB 初始化或清理失败：', error);
            }
            let remote: CollectionBoxTaskDraft | null = null;
            try { remote = await loadServerBrowserTask<CollectionBoxTaskDraft>(); } catch (_) { /* 本地草稿仍可恢复 */ }
            const compatibleRemote = remote?.version === 1
                && remote.sourceStatus === collectionStatus
                && ['running', 'interrupted', 'awaiting_confirmation', 'failed'].includes(remote.status)
                && !isCollectionBoxTaskAbandoned(remote.taskId)
                ? remote
                : null;
            const compatibleLocal = local && !isCollectionBoxTaskAbandoned(local.taskId) ? local : null;
            if (local && !compatibleLocal) {
                try { await deleteCollectionBoxTaskDraft(local.taskId); } catch (_) { /* 放弃标记仍会阻止恢复 */ }
            }
            if (remote && isCollectionBoxTaskAbandoned(remote.taskId)) {
                try { await deleteServerBrowserTask(); } catch (_) { /* 下次进入时继续清理 */ }
            }
            const draft = !compatibleLocal || (compatibleRemote && compatibleRemote.updatedAt > compatibleLocal.updatedAt) ? compatibleRemote : compatibleLocal;
            if (!draft || cancelled) return;
            if (draft.status === 'running') draft.status = 'interrupted';
            activeDraftRef.current = draft;
            try { activeDraftRef.current = await saveCollectionBoxTaskDraft(draft); }
            catch (error) { console.warn('[AI任务草稿] 服务器草稿恢复成功，但无法写回 IndexedDB：', error); }
            useDataStore.setState({
                rows: draft.rows,
                originalRows: draft.originalRows,
                fields: draft.fields,
                description: draft.description,
                fileName: draft.fileName,
                hasData: draft.rows.length > 0,
                cellErrors: draft.cellErrors,
                filteredRowIndices: new Set(draft.filteredRowIndices),
                imageReplacements: new Map(draft.imageReplacements),
                productFlags: draft.productFlags,
            });
            setSelectedProducts(new Set(draft.selectedProductIds));
            setSelectedTasks(new Set(draft.taskTypes));
            setRecoveryDraft(draft);
        };
        const onDataLoaded = () => { void restore(); };
        window.addEventListener('collection-box:data-loaded', onDataLoaded);
        return () => { cancelled = true; window.removeEventListener('collection-box:data-loaded', onDataLoaded); };
    }, [collectionStatus, sourceMode]);

    const toggleTask = (type: 'title' | 'filter' | 'size' | 'aiImage' | 'sku-spec' | 'sku-weight') => {
        setSelectedTasks(prev => {
            const next = new Set(prev);
            if (next.has(type)) next.delete(type);
            else next.add(type);
            return next;
        });
    };

    const executeTasks = async (types: PersistedBatchTaskType[], productIds: Set<string>, resume = false) => {
        if (types.length === 0) return;

        if (!resume) {
            const now = Date.now();
            const data = useDataStore.getState();
            try {
                activeDraftRef.current = await saveCollectionBoxTaskDraft({
                version: 1,
                taskId: `ai-${now}-${Math.random().toString(36).slice(2, 8)}`,
                sourceStatus: collectionStatus,
                taskTypes: types,
                selectedProductIds: Array.from(productIds),
                completedProductTasks: [],
                status: 'running',
                unsavedChanges: true,
                rows: data.rows,
                originalRows: data.originalRows,
                fields: data.fields,
                description: data.description,
                fileName: data.fileName,
                cellErrors: data.cellErrors,
                filteredRowIndices: Array.from(data.filteredRowIndices),
                imageReplacements: Array.from(data.imageReplacements),
                productFlags: data.productFlags,
                progress: { completed: 0, total: Math.max(1, productIds.size * types.length), message: '准备处理...' },
                createdAt: now,
                updatedAt: now,
                    estimatedBytes: 0,
                });
            } catch (error) {
                setSaveError('无法创建浏览器本地任务草稿，已取消本次 AI 处理。请检查浏览器是否允许使用 IndexedDB。');
                console.error('[AI任务草稿] 创建失败：', error);
                return;
            }
            checkpointCountRef.current = 0;
            try { await checkpointServerBrowserTask(activeDraftRef.current); }
            catch (error) { console.warn('[AI任务草稿] 首次服务器暂存失败，继续使用 IndexedDB：', error); }
        }

        const activeProductIds = activeDraftRef.current?.selectedProductIds ?? Array.from(productIds);
        const activeIndices = groups
            .filter(group => activeProductIds.includes(group.productId))
            .flatMap(group => group.rows.map(row => row.index));

        const otherTypes = types.filter(t => t !== 'aiImage') as Array<'title' | 'filter' | 'size' | 'sku-spec' | 'sku-weight'>;
        const hasAIImage = types.includes('aiImage');

        // AI 编辑首图先执行（不弹窗）
        if (hasAIImage) {
            await handleAIEditFirstImages(activeIndices);
        }

        // 执行其他任务
        if (otherTypes.length > 0) {
            if (stepConfirmEnabled) {
                await runStepBatch(otherTypes, activeIndices, false);
                // 步进确认模式：StepConfirmModal 会自动弹出，不再额外弹 CompareModal
            } else {
                for (const type of otherTypes) {
                    await runBatch(type, activeIndices, false);
                }
                setCompareOpen(true);
            }
        } else {
            // 只有 AI 编辑任务
            if (stepConfirmEnabled) {
                // 如果开启了步进确认，为了保持体验一致，我们也构造一个 StepConfirmModal 的数据
                const store = useDataStore.getState();
                const allIndices = activeIndices;
                const map = new Map<string, number[]>();
                allIndices.forEach(idx => {
                    const row = store.rows[idx];
                    if (!row) return;
                    const pid = (row.productId ?? '').trim() || `__noid_${idx}__`;
                    if (!map.has(pid)) map.set(pid, []);
                    map.get(pid)!.push(idx);
                });
                
                const completedResults: any[] = [];
                let i = 0;
                for (const [pid, indices] of map.entries()) {
                    completedResults.push({
                        productId: pid,
                        productIndex: i++,
                        totalProducts: map.size,
                        taskResults: [],
                        totalDurationMs: 0,
                        snapshotBefore: indices.map(idx => store.originalRows[idx]),
                        snapshotAfter: indices.map(idx => store.rows[idx]),
                        originalIndices: indices,
                    });
                }
                
                useBatchStore.setState(state => ({
                    stepState: {
                        active: true,
                        processing: false,
                        cancelled: false,
                        completedResults,
                        currentProductIndex: map.size - 1,
                        totalProducts: map.size
                    }
                }));
            } else {
                // 弹前后对比
                setCompareOpen(true);
            }
        }
        await persistActiveDraft('awaiting_confirmation', true);
    };

    const handleExecute = async () => {
        setBrowserTaskRunning(true);
        try { await executeTasks(Array.from(selectedTasks), selectedProducts); }
        finally { setBrowserTaskRunning(false); }
    };

    const continueRecoveredTask = async () => {
        const draft = recoveryDraft;
        if (!draft) return;
        setRecoveryDraft(null);
        draft.status = 'running';
        activeDraftRef.current = draft;
        await persistActiveDraft('running', true);
        setBrowserTaskRunning(true);
        try { await executeTasks(draft.taskTypes, new Set(draft.selectedProductIds), true); }
        finally { setBrowserTaskRunning(false); }
    };

    const abandonRecoveredTask = async () => {
        const draft = recoveryDraft;
        if (!draft) return;
        activeDraftRef.current = null;
        markCollectionBoxTaskAbandoned(draft.taskId);
        try { await deleteCollectionBoxTaskDraft(draft.taskId); } catch (_) { /* 放弃标记仍会阻止恢复 */ }
        try { await deleteServerBrowserTask(); } catch (_) { /* 本地放弃不受服务端异常影响 */ }
        setRecoveryDraft(null);
    };

    const modifiedCount = useMemo(() => {
        let count = 0;
        for (let i = 0; i < rows.length; i++) {
            const cur = rows[i]; const orig = originalRows[i];
            if (!orig) { count++; continue; }
            if (displayFields.some(f => (cur[f.key] ?? '') !== (orig[f.key] ?? ''))) count++;
        }
        return count;
    }, [rows, originalRows, displayFields]);

    const hasSelection = selectedProducts.size > 0;
    // 选中产品的旗子：若全部一致则显示该色，否则显示默认灰色
    const selectedFlag: FlagColor = useMemo(() => {
        if (selectedProducts.size === 0) return DEFAULT_FLAG;
        const flags = Array.from(selectedProducts).map(id => productFlags[id] ?? DEFAULT_FLAG);
        const first = flags[0];
        return flags.every(f => f === first) ? first : DEFAULT_FLAG;
    }, [selectedProducts, productFlags]);
    const selectedLLM = llmInstances.find(i => i.id === titleConfig.llmInstanceId) ?? llmInstances[0];
    const hasLLM = !!selectedLLM && !!titleConfig.model;
    const hasOCR = !!selectOCRInstance(ocrInstances);

    // SKU 规格优化 / 估重 的 LLM 可用性
    const skuSpecLLM = llmInstances.find(i => i.id === skuConfig.specLlmInstanceId) ?? llmInstances[0];
    const skuSpecHasLLM = !!skuSpecLLM && !!skuConfig.specModel && skuConfig.specFields.length > 0;
    const skuWeightLLM = llmInstances.find(i => i.id === skuConfig.weightLlmInstanceId) ?? llmInstances[0];
    const skuWeightHasLLM = !!skuWeightLLM && !!skuConfig.weightModel;

    // AI 编辑首图 - 执行逻辑（可传入 indicesOverride 仅作用于指定行，否则用选中行）
    const handleAIEditFirstImages = useCallback(async (indicesOverride?: number[]) => {
        const instances = getSelectedImageEditInstances(imageEditInstances);
        const instance = instances[0];
        const prompt = (imageEditSettings.presetPrompts ?? [])[0] ?? '';
        const targetIndices = indicesOverride ?? selectedRowIndices;
        if (!instance || !prompt.trim() || targetIndices.length === 0) return;

        // 收集目标行的唯一首图
        const entryByUrl = new Map<string, { rowIndex: number; url: string; productIds: string[] }>();
        for (const ri of targetIndices) {
            const url = rows[ri]?.firstImage ?? parseImageUrls(rows[ri]?.mainImages ?? '')[0] ?? '';
            if (!url) continue;
            const productId = (rows[ri]?.productId ?? '').trim() || `__noid_${ri}__`;
            if (shouldSkipBatchProduct('aiImage', productId)) continue;
            const entry = entryByUrl.get(url);
            if (entry) {
                if (!entry.productIds.includes(productId)) entry.productIds.push(productId);
            } else {
                entryByUrl.set(url, { rowIndex: ri, url, productIds: [productId] });
            }
        }
        const entries = Array.from(entryByUrl.values());
        if (entries.length === 0) return;

        setAiEditRunning(true);
        setAiEditProgress(`0/${entries.length}`);
        const batchStore = useBatchStore.getState();
        const progressTask = batchStore.startTask('AI 编辑首图', entries.length, `准备处理 ${entries.length} 张首图...`);
        const startedAt = Date.now();
        let completed = 0;
        let failed = 0;
        const elapsedTimer = window.setInterval(() => {
            const liveStore = useBatchStore.getState();
            const liveTask = liveStore.tasks.get(progressTask.id);
            if (!liveTask?.running) return;
            liveStore.updateProgress(progressTask.id, {
                ...liveTask.progress,
                elapsedMs: Date.now() - startedAt,
            }, true);
        }, 1000);

        try {
            for (let idx = 0; idx < entries.length; idx++) {
                if (progressTask.signal.aborted) break;
                const { url, productIds } = entries[idx];
                batchStore.updateProgress(progressTask.id, {
                    current: completed,
                    total: entries.length,
                    message: `正在处理第 ${idx + 1}/${entries.length} 张首图...`,
                    elapsedMs: Date.now() - startedAt,
                }, true);
                try {
                const proxiedUrlString = url.startsWith('http') ? `/api/proxy?target=${encodeURIComponent(url)}` : url;
                const imgResp = await fetch(proxiedUrlString);
                const imgBlob = await imgResp.blob();
                const imgFile = new File([imgBlob], 'first-image.png', { type: imgBlob.type || 'image/png' });

                const generated = await generateImageWithFallback(
                    instances,
                    { prompt: prompt.trim(), images: [imgFile], ratio: imageEditSettings.defaultRatio || instance.defaultRatio, quality: imageEditSettings.defaultQuality || instance.defaultQuality, count: imageEditSettings.defaultCount || 1 },
                    (_status, activeInstance, order) => {
                        batchStore.updateProgress(progressTask.id, {
                            current: completed,
                            total: entries.length,
                            message: `正在使用${order > 1 ? '备用' : ''}实例 ${activeInstance.name} 处理第 ${idx + 1}/${entries.length} 张首图...`,
                            elapsedMs: Date.now() - startedAt,
                        }, true);
                    },
                );
                const { job, instance: usedInstance } = generated;

                const resultItem = job.result?.data?.[0] ?? job.result?.images?.[0];
                if (!resultItem) throw new Error('无生成结果');

                const resultSource = resultItem.b64_json
                    ? `data:image/png;base64,${resultItem.b64_json}`
                    : resultItem.url
                        ? (resultItem.url.startsWith('http')
                            ? resultItem.url
                            : `${usedInstance.apiUrl.replace(/\/$/, '')}${resultItem.url.startsWith('/') ? '' : '/'}${resultItem.url}`)
                        : '';
                if (!resultSource) throw new Error('无有效结果 URL 或 Base64');

                let newUrl: string;
                if (imageEditSettings.browserCompressionEnabled) {
                    batchStore.updateProgress(progressTask.id, {
                        current: completed,
                        total: entries.length,
                        message: `正在使用浏览器压缩第 ${idx + 1}/${entries.length} 张图片...`,
                        elapsedMs: Date.now() - startedAt,
                    }, true);
                    const compressed = await compressImageInBrowser(resultSource, compressionMaxBytes(imageEditSettings.compressionLevel));
                    newUrl = imageEditSettings.jdcloudStorageEnabled
                        ? await uploadImageToStorage({
                            fileName: compressed.fileName,
                            fileBase64: compressed.fileBase64,
                            contentType: compressed.contentType,
                        })
                        : compressed.dataUrl;
                } else if (imageEditSettings.jdcloudStorageEnabled) {
                    newUrl = resultItem.b64_json
                        ? await uploadImageToStorage({
                            fileName: `ai-first-image-${Date.now()}.png`,
                            fileBase64: resultItem.b64_json,
                            contentType: 'image/png',
                        })
                        : await uploadImageToStorage({ imageUrl: resultSource });
                } else {
                    newUrl = resultSource;
                }


                // 替换所有使用该首图的行
                for (let r = 0; r < rows.length; r++) {
                    const curFirst = rows[r].firstImage ?? parseImageUrls(rows[r].mainImages ?? '')[0] ?? '';
                    if (curFirst === url) updateCell(r, 'firstImage', newUrl);
                }
                recordImageReplacement(url, newUrl);
                } catch (err) {
                    failed++;
                    console.error(`[AI编辑首图] 第${idx + 1}张失败:`, err);
                }
                completed = idx + 1;
                if (!progressTask.signal.aborted) {
                    for (const productId of productIds) await markBatchProductCompleted('aiImage', productId);
                }
                setAiEditProgress(`${completed}/${entries.length}`);
                batchStore.updateProgress(progressTask.id, {
                    current: completed,
                    total: entries.length,
                    message: failed > 0 ? `已处理 ${completed}/${entries.length} 张，失败 ${failed} 张` : `已处理 ${completed}/${entries.length} 张首图`,
                    elapsedMs: Date.now() - startedAt,
                }, completed < entries.length && !progressTask.signal.aborted);
            }
        } finally {
            window.clearInterval(elapsedTimer);
            const cancelled = progressTask.signal.aborted;
            batchStore.finishTask(progressTask.id, {
                current: completed,
                total: entries.length,
                elapsedMs: Date.now() - startedAt,
                message: cancelled
                    ? `已取消，完成 ${completed}/${entries.length} 张`
                    : failed > 0
                        ? `处理完成：成功 ${completed - failed} 张，失败 ${failed} 张`
                        : `处理完成：共 ${completed} 张首图`,
            });
            setAiEditRunning(false);
        }
    }, [imageEditInstances, imageEditSettings, rows, selectedRowIndices, updateCell, recordImageReplacement]);

    // 获取某产品所有行的索引（与分组口径一致）
    const getProductIndices = useCallback((pid: string): number[] => {
        const indices: number[] = [];
        rows.forEach((row, i) => {
            const rpid = (row.productId ?? '').trim() || `__noid_${i}__`;
            if (rpid === pid) indices.push(i);
        });
        return indices;
    }, [rows]);

    const openCommonItemEditor = useCallback((itemId: string) => {
        if (sourceMode !== 'collection-box' || window.parent === window || !/^\d+$/.test(itemId)) return;
        const parentOrigin = new URLSearchParams(window.location.search).get('parent_origin');
        if (!parentOrigin) return;
        window.parent.postMessage({
            source: 'tabbygrid',
            type: 'collection-box:edit-item',
            itemId,
        }, parentOrigin);
    }, [sourceMode]);

    // 供对比弹窗调用：仅对该产品重跑 AI 优化标题 / AI 编辑首图
    const runAiTitleForProduct = useCallback((pid: string) => {
        const indices = getProductIndices(pid);
        if (indices.length > 0) runBatch('title', indices, false);
    }, [getProductIndices, runBatch]);

    const runAiImageForProduct = useCallback((pid: string) => {
        const indices = getProductIndices(pid);
        if (indices.length > 0) handleAIEditFirstImages(indices);
    }, [getProductIndices, handleAIEditFirstImages]);

    const allToggleFields = useMemo(() => [FIRST_IMAGE_FIELD, ...EXCEL_FIELDS], []);
    const activeSearchFilterCount = Number(Boolean(search.trim()))
        + Number(Boolean(createdFrom || createdTo))
        + Number(Boolean(processedFrom || processedTo))
        + Number(Boolean(flagFilter));
    const applySearch = () => setAppliedSearch({ search: search.trim(), createdFrom, createdTo, processedFrom, processedTo, flagFilter });
    const resetSearch = () => {
        setSearch(''); setCreatedFrom(''); setCreatedTo(''); setProcessedFrom(''); setProcessedTo(''); setFlagFilter(null);
        setAppliedSearch({ search: '', createdFrom: '', createdTo: '', processedFrom: '', processedTo: '', flagFilter: null });
    };
    const applyCollectionSearch = () => {
        setSelectedProducts(new Set());
        setGroupPage(1);
        loadCollectionBox(collectionFilters, 1, sourcePagination.per_page);
    };
    const resetCollectionSearch = () => {
        const filters: CollectionBoxFilters = {
            keyword: '', source_item_id: '', source_platform: '', price_min: '', price_max: '',
            weight_min: '', weight_max: '', group_id: '', notes: '', is_claimed_shopee: '', claim_platform: '',
            time_field: 'created_at', time_start: '', time_end: '',
        };
        setCollectionFilters(filters);
        setSelectedProducts(new Set());
        setGroupPage(1);
        loadCollectionBox(filters, 1, sourcePagination.per_page);
    };
    const loadCollectionPage = (page: number, perPage = sourcePagination.per_page) => {
        setSelectedProducts(new Set());
        setJumpPage('');
        loadCollectionBox(collectionFilters, page, perPage);
    };

    useEffect(() => {
        if (sourceMode !== 'collection-box' || window.parent === window) return undefined;
        const parentOrigin = new URLSearchParams(window.location.search).get('parent_origin');
        if (!parentOrigin) return undefined;

        const onCommand = (event: MessageEvent) => {
            if (event.origin !== parentOrigin || event.source !== window.parent || event.data?.source !== 'tshopee' || event.data?.type !== 'collection-box:command') return;
            switch (event.data.command) {
                case 'toggle-filters':
                    setCollectionFiltersCollapsed(value => !value);
                    break;
                case 'toggle-columns':
                    setColVisOpen(value => !value);
                    break;
                case 'compare':
                    setCompareOpen(true);
                    break;
                case 'reset':
                    resetToOriginal();
                    break;
                case 'clear-selection':
                    setSelectedProducts(new Set());
                    break;
            }
        };

        window.addEventListener('message', onCommand);
        return () => window.removeEventListener('message', onCommand);
    }, [resetToOriginal, sourceMode]);

    useEffect(() => {
        if (sourceMode !== 'collection-box' || window.parent === window) return;
        const parentOrigin = new URLSearchParams(window.location.search).get('parent_origin');
        if (!parentOrigin) return;
        const itemIds = Array.from(new Set(groups
            .filter(group => selectedProducts.has(group.productId))
            .flatMap(group => group.rows.map(({ row }) => String(row.__commonItemId ?? '')))
            .filter(id => /^\d+$/.test(id) && Number(id) > 0)));
        window.parent.postMessage({
            source: 'tabbygrid',
            type: 'collection-box:selection',
            itemIds,
        }, parentOrigin);
    }, [groups, selectedProducts, sourceMode]);

    useEffect(() => {
        if (sourceMode !== 'collection-box' || window.parent === window) return;
        const parentOrigin = new URLSearchParams(window.location.search).get('parent_origin');
        if (!parentOrigin) return;
        window.parent.postMessage({
            source: 'tabbygrid',
            type: 'collection-box:ui-state',
            filtersCollapsed: collectionFiltersCollapsed,
        }, parentOrigin);
    }, [collectionFiltersCollapsed, sourceMode]);

    useEffect(() => {
        if (sourceMode !== 'collection-box' || window.parent === window) return;
        const parentOrigin = new URLSearchParams(window.location.search).get('parent_origin');
        if (!parentOrigin) return;
        window.parent.postMessage({
            source: 'tabbygrid',
            type: 'collection-box:ui-state',
            comparisonOpen: compareOpen || compareProductId !== null,
        }, parentOrigin);
    }, [compareOpen, compareProductId, sourceMode]);

    return (
        <div className={cn('flex h-full flex-col bg-white', sourceMode === 'collection-box' && 'collection-box-mode')}>
            {/* 顶部工具栏 */}
            <div className={cn('shrink-0 flex-wrap items-center gap-3 border-b border-[#e7ecee] bg-white px-6 py-4', sourceMode === 'collection-box' && collectionFiltersCollapsed ? 'hidden' : 'flex')}>
                {sourceMode !== 'collection-box' && (
                    <>
                        <span className="text-xl font-bold tracking-tight text-[#202b31]">AI采集箱</span>
                        <span className="text-xs text-[#8a989e]">
                            共 <span className="font-semibold text-[#159b96]">{groups.length}</span> 个产品 · <span className="font-semibold text-[#159b96]">{rows.length}</span> 个 SKU
                            {modifiedCount > 0 && <span className="ml-2 rounded-full bg-[#fff3d8] px-2 py-0.5 text-xs font-medium text-[#a96d00]">已修改 {modifiedCount} 行</span>}
                        </span>
                    </>
                )}
                {sourceMode === 'collection-box' ? (
                    !collectionFiltersCollapsed && (
                    <div className="basis-full grid flex-1 grid-cols-1 gap-2 pt-1 xl:grid-cols-4">
                        <CollectionFilterGroup label="关联货源:">
                            <select value={collectionFilters.source_platform} onChange={(e) => setCollectionFilters(v => ({ ...v, source_platform: e.target.value }))} className="h-full min-w-0 flex-1 bg-transparent px-2 text-xs outline-none">
                                <option value="">全部关联货源</option>
                                {sourcePlatforms.map(platform => <option key={platform} value={platform}>{platform}</option>)}
                            </select>
                        </CollectionFilterGroup>
                        <CollectionFilterGroup label="产品标题:">
                            <input value={collectionFilters.keyword} onChange={(e) => setCollectionFilters(v => ({ ...v, keyword: e.target.value }))} placeholder="多个用逗号分隔，支持Excel复制粘贴" className="h-full min-w-0 flex-1 px-2 text-xs outline-none" />
                        </CollectionFilterGroup>
                        <CollectionFilterGroup label="货源ID:">
                            <input value={collectionFilters.source_item_id} onChange={(e) => setCollectionFilters(v => ({ ...v, source_item_id: e.target.value }))} placeholder="多个用逗号分隔，支持Excel复制粘贴" className="h-full min-w-0 flex-1 px-2 text-xs outline-none" />
                        </CollectionFilterGroup>
                        <CollectionFilterGroup label="货源价格:">
                            <input value={collectionFilters.price_min} onChange={(e) => setCollectionFilters(v => ({ ...v, price_min: e.target.value }))} placeholder="价格" inputMode="decimal" className="h-full min-w-0 flex-1 px-2 text-xs outline-none" />
                            <span className="text-zinc-300">-</span>
                            <input value={collectionFilters.price_max} onChange={(e) => setCollectionFilters(v => ({ ...v, price_max: e.target.value }))} placeholder="价格" inputMode="decimal" className="h-full min-w-0 flex-1 px-2 text-xs outline-none" />
                            <span className="pr-2 text-xs text-zinc-500">元</span>
                        </CollectionFilterGroup>
                        <CollectionFilterGroup label="本地分组:">
                            <select value={collectionFilters.group_id} onChange={(e) => setCollectionFilters(v => ({ ...v, group_id: e.target.value }))} className="h-full min-w-0 flex-1 bg-transparent px-2 text-xs outline-none">
                                <option value="">全部分组</option><option value="ungrouped">未分组</option>
                                {groupOptions.map(group => <option key={group.id} value={group.id}>{group.name}</option>)}
                            </select>
                        </CollectionFilterGroup>
                        <CollectionFilterGroup label="备注:">
                            <input value={collectionFilters.notes} onChange={(e) => setCollectionFilters(v => ({ ...v, notes: e.target.value }))} placeholder="多个用逗号分隔，支持Excel复制粘贴" className="h-full min-w-0 flex-1 px-2 text-xs outline-none" />
                        </CollectionFilterGroup>
                        <CollectionFilterGroup>
                            <select value={collectionFilters.time_field} onChange={(e) => setCollectionFilters(v => ({ ...v, time_field: e.target.value as CollectionBoxFilters['time_field'] }))} className="h-full bg-transparent px-2 text-xs outline-none">
                                <option value="created_at">创建时间</option><option value="updated_at">更新时间</option>
                            </select>
                            <input type="date" value={collectionFilters.time_start} onChange={(e) => setCollectionFilters(v => ({ ...v, time_start: e.target.value }))} className="h-full min-w-0 flex-1 text-xs outline-none" />
                            <span className="text-zinc-300">-</span>
                            <input type="date" value={collectionFilters.time_end} onChange={(e) => setCollectionFilters(v => ({ ...v, time_end: e.target.value }))} className="h-full min-w-0 flex-1 text-xs outline-none" />
                        </CollectionFilterGroup>
                        <CollectionFilterGroup label="认领平台:">
                            <select value={collectionFilters.is_claimed_shopee} onChange={(e) => setCollectionFilters(v => ({ ...v, is_claimed_shopee: e.target.value }))} className="h-full min-w-0 flex-1 bg-transparent px-2 text-xs outline-none">
                                <option value="">请选择</option><option value="1">已认领平台</option><option value="0">未认领平台</option>
                            </select>
                            <select value={collectionFilters.claim_platform} onChange={(e) => setCollectionFilters(v => ({ ...v, claim_platform: e.target.value }))} className="h-full min-w-0 flex-1 bg-transparent px-2 text-xs outline-none">
                                <option value="">请选择</option><option value="shopee">Shopee</option>
                            </select>
                        </CollectionFilterGroup>
                        <CollectionFilterGroup label="重量:">
                            <input value={collectionFilters.weight_min} onChange={(e) => setCollectionFilters(v => ({ ...v, weight_min: e.target.value }))} placeholder="最低值" inputMode="decimal" className="h-full min-w-0 flex-1 px-2 text-xs outline-none" />
                            <span className="text-zinc-300">-</span>
                            <input value={collectionFilters.weight_max} onChange={(e) => setCollectionFilters(v => ({ ...v, weight_max: e.target.value }))} placeholder="最高值" inputMode="decimal" className="h-full min-w-0 flex-1 px-2 text-xs outline-none" />
                        </CollectionFilterGroup>
                        <div className="flex h-8 items-center gap-3">
                            <Button variant="primary" size="sm" onClick={applyCollectionSearch} disabled={sourceLoading} className="h-8 bg-[#159b96] px-4 text-white hover:bg-[#11837f]">
                                {sourceLoading ? <Loader2 size={13} className="animate-spin" /> : <Search size={13} />} 搜索
                            </Button>
                            <button type="button" onClick={resetCollectionSearch} disabled={sourceLoading} className="h-8 rounded border border-zinc-300 px-4 text-xs text-zinc-600 hover:bg-zinc-50 disabled:opacity-50">重置</button>
                            <button type="button" onClick={() => setCollectionFiltersCollapsed(true)} className="inline-flex h-8 items-center gap-1 text-xs text-[#159b96]">
                                收起筛选 <ChevronUp size={16} strokeWidth={2.25} className="shrink-0" />
                            </button>
                        </div>
                    </div>
                    )
                ) : (
                <div className="ml-4 flex flex-wrap items-center gap-2">
                    <div className="relative">
                        <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
                        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="标题 / 产品 ID" aria-label="标题或产品 ID"
                            className="h-8 w-40 rounded-md border border-[#cfd8dc] bg-white pl-8 pr-2 text-xs text-zinc-600 outline-none focus:border-[#159b96]" />
                    </div>
                    <div className="flex h-8 items-center gap-1 rounded-md border border-[#d7e2e4] bg-white px-2">
                        <CalendarDays size={12} className="text-zinc-400" />
                        <span className="shrink-0 text-[11px] text-zinc-500">创建</span>
                        <input type="date" value={createdFrom} onInput={(e) => setCreatedFrom(e.currentTarget.value)} onChange={(e) => setCreatedFrom(e.target.value)} aria-label="创建时间开始"
                            className="w-[108px] bg-transparent text-[11px] text-zinc-600 outline-none" />
                        <span className="text-zinc-300">-</span>
                        <input type="date" value={createdTo} onInput={(e) => setCreatedTo(e.currentTarget.value)} onChange={(e) => setCreatedTo(e.target.value)} aria-label="创建时间结束"
                            className="w-[108px] bg-transparent text-[11px] text-zinc-600 outline-none" />
                    </div>
                    <div className="flex h-8 items-center gap-1 rounded-md border border-[#d7e2e4] bg-white px-2">
                        <CalendarDays size={12} className="text-zinc-400" />
                        <span className="shrink-0 text-[11px] text-zinc-500">处理</span>
                        <input type="date" value={processedFrom} onInput={(e) => setProcessedFrom(e.currentTarget.value)} onChange={(e) => setProcessedFrom(e.target.value)} aria-label="处理时间开始"
                            className="w-[108px] bg-transparent text-[11px] text-zinc-600 outline-none" />
                        <span className="text-zinc-300">-</span>
                        <input type="date" value={processedTo} onInput={(e) => setProcessedTo(e.currentTarget.value)} onChange={(e) => setProcessedTo(e.target.value)} aria-label="处理时间结束"
                            className="w-[108px] bg-transparent text-[11px] text-zinc-600 outline-none" />
                    </div>
                    <div className="flex h-8 items-center gap-1 rounded-md border border-[#d7e2e4] bg-white px-1.5">
                        <span className="px-0.5 text-[11px] text-zinc-500">小旗</span>
                        <button type="button" title="全部小旗" onClick={() => setFlagFilter(null)}
                            className={cn('flex h-6 min-w-7 items-center justify-center rounded px-1 text-[10px]', !flagFilter ? 'bg-[#e8f7f6] text-[#159b96]' : 'text-zinc-400 hover:bg-zinc-50')}>全部</button>
                        {FLAG_OPTIONS.map(option => (
                            <button key={option.color} type="button" title={option.label} onClick={() => setFlagFilter(option.color)}
                                className={cn('flex h-6 w-6 items-center justify-center rounded', flagFilter === option.color ? 'bg-[#e8f7f6] ring-1 ring-[#8dd4d1]' : 'hover:bg-zinc-50')}>
                                <FlagIcon color={option.color} size={13} />
                            </button>
                        ))}
                    </div>
                    <Button variant="primary" size="sm" onClick={applySearch} className="h-8 bg-[#159b96] px-3 text-white hover:bg-[#11837f]">
                        <Search size={13} /> 搜索
                    </Button>
                    {activeSearchFilterCount > 0 && (
                        <button type="button" title="重置搜索条件" onClick={resetSearch}
                            className="flex h-8 w-8 items-center justify-center rounded-md border border-[#d7e2e4] text-zinc-400 hover:bg-zinc-50 hover:text-[#159b96]">
                            <RotateCcw size={13} />
                        </button>
                    )}
                </div>
                )}
                {sourceMode !== 'collection-box' && (
                    <>
                        <div className="mx-1 h-5 w-px bg-zinc-200" />
                        <div className="relative">
                            <Button variant="outline" size="sm" onClick={() => setColVisOpen(v => !v)} className="border-[#9edbd8] text-[#159b96] hover:bg-[#effafa]">
                                <Columns3 size={14} /> 列设置
                            </Button>
                            {colVisOpen && (
                                <ColumnSettingsDropdown
                                    fields={allToggleFields}
                                    hiddenFields={hiddenFields}
                                    fieldOrder={fieldOrder}
                                    onToggle={toggleFieldVisibility}
                                    onReset={setAllFieldsVisible}
                                    onReorder={setFieldOrder}
                                    onResetOrder={resetFieldOrder}
                                    onClose={() => setColVisOpen(false)}
                                />
                            )}
                        </div>
                        <Button variant="outline" size="sm" onClick={() => setCompareOpen(true)} className="border-[#9edbd8] text-[#159b96] hover:bg-[#effafa]"><GitCompare size={14} /> 前后对比</Button>
                        <Button variant="outline" size="sm" onClick={resetToOriginal} className="border-[#9edbd8] text-[#159b96] hover:bg-[#effafa]"><RotateCcw size={14} /> 重置</Button>
                    </>
                )}
            </div>
            {sourceMode === 'collection-box' && colVisOpen && (
                <div className="fixed right-4 top-0 z-[300] h-0 w-64">
                    <ColumnSettingsDropdown
                        fields={allToggleFields}
                        hiddenFields={hiddenFields}
                        fieldOrder={fieldOrder}
                        onToggle={toggleFieldVisibility}
                        onReset={setAllFieldsVisible}
                        onReorder={setFieldOrder}
                        onResetOrder={resetFieldOrder}
                        onClose={() => setColVisOpen(false)}
                    />
                </div>
            )}

            {/* 选中产品操作栏 - 始终显示 */}
            <div className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-2 border-b border-[#dceeed] bg-[#f5fbfb] px-6 py-3">
                {/* 文件操作 */}
                <div className="flex items-center gap-2">
                    <input
                        ref={toolbarFileInputRef}
                        type="file"
                        accept=".xlsx,.xls"
                        onChange={(e) => { if (e.target.files?.[0]) handleFileImport(e.target.files[0]); }}
                        className="hidden"
                    />
                    {sourceMode === 'collection-box' ? (
                        <Button variant="primary" size="sm" onClick={handleCollectionBoxSave} disabled={sourceSaving}>
                            {sourceSaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                            {sourceSaving ? '保存中…' : '保存ai修改'}
                        </Button>
                    ) : (
                        <>
                            <Button variant="outline" size="sm" onClick={() => toolbarFileInputRef.current?.click()} className="border-[#9edbd8] text-[#159b96]"><FileUp size={14} /> 导入 Excel</Button>
                            <Button variant="outline" size="sm" onClick={handleExportSelected} className="border-[#9edbd8] text-[#159b96]"><FileDown size={14} /> {selectedProducts.size > 0 ? '导出选中' : '导出全部'}</Button>
                        </>
                    )}
                </div>

                <div className="h-5 w-px bg-[#cfe8e6]" />

                {/* 选中状态 + 标记 */}
                {hasSelection ? (
                    <div className="flex items-center gap-2">
                        <div className="flex items-center gap-1.5 rounded-full bg-[#dff3f1] py-1 pl-2.5 pr-1 text-xs font-medium text-[#147f7b]">
                            <span>已选 <span className="font-bold">{selectedProducts.size}</span> 个产品</span>
                            <span className="text-[#61aaa6]">· {selectedRowIndices.length} 行</span>
                            <button onClick={deselectAll} className="rounded p-0.5 text-[#61aaa6] transition-colors hover:bg-[#cce9e7] hover:text-[#147f7b]" title="取消选择"><X size={12} /></button>
                        </div>
                        <div className="flex items-center gap-1.5">
                            <span className="text-xs text-[#61aaa6]">标记</span>
                            <FlagSelector
                                value={selectedFlag}
                                onSelect={(c) => setProductFlag(Array.from(selectedProducts), c)}
                                title="为选中产品打旗子"
                            />
                        </div>
                    </div>
                ) : (
                    <div className="flex items-center gap-1.5 text-xs text-[#61aaa6]">
                        <CheckSquare size={14} className="text-[#61aaa6]" /> 未选中产品
                    </div>
                )}

                <div className="h-5 w-px bg-[#cfe8e6]" />

                {/* AI 任务组 */}
                <div className="flex flex-wrap items-center gap-1.5">
                    <TaskChip active={selectedTasks.has('title')} disabled={!hasLLM || !hasSelection || stepState.processing || aiEditRunning} icon={<Sparkles size={14} />} label="优化标题" onClick={() => toggleTask('title')} />
                    <TaskChip active={selectedTasks.has('filter')} disabled={!hasOCR || !hasSelection || stepState.processing || aiEditRunning} icon={<FilterX size={14} />} label="过滤低质量" onClick={() => toggleTask('filter')} />
                    <TaskChip active={selectedTasks.has('size')} disabled={!hasOCR || !hasSelection || stepState.processing || aiEditRunning} icon={<Ruler size={14} />} label="识别尺码图" onClick={() => toggleTask('size')} />
                    <TaskChip active={selectedTasks.has('sku-spec')} disabled={!skuSpecHasLLM || !hasSelection || stepState.processing || aiEditRunning} icon={<Tags size={14} />} label="SKU规格优化" title={skuSpecHasLLM ? '选中 SKU 规格字段调用 LLM 优化' : '请在「SKU 配置」中启用 SKU 规格优化并选择字段与模型'} onClick={() => toggleTask('sku-spec')} />
                    <TaskChip active={selectedTasks.has('sku-weight')} disabled={!skuWeightHasLLM || !hasSelection || stepState.processing || aiEditRunning} icon={<Scale size={14} />} label="SKU估重" title={skuWeightHasLLM ? '调用 LLM 估算每个 SKU 重量' : '请在「SKU 配置」中启用 SKU 估重并选择模型'} onClick={() => toggleTask('sku-weight')} />
                    <TaskChip active={selectedTasks.has('aiImage')} disabled={!hasSelection || imageEditInstances.length === 0 || aiEditRunning} icon={<Sparkles size={14} />} label="AI 编辑首图" title={imageEditInstances.length > 0 ? '选中首图调用 AI 图片编辑（使用配置中的提示词）' : '请在「AI 图片编辑」中配置实例与提示词'} onClick={() => toggleTask('aiImage')} />
                </div>

                {/* 右侧：执行 / 步进确认 / 全选 */}
                <div className="ml-auto flex items-center gap-2">
                    <label className="flex cursor-pointer items-center gap-1.5 text-xs text-[#159b96] select-none">
                        <button
                            role="switch"
                            aria-checked={stepConfirmEnabled}
                            disabled={browserTaskRunning || stepState.active || aiEditRunning}
                            onClick={toggleStepConfirm}
                            className={cn(
                                'relative inline-flex h-4 w-7 shrink-0 cursor-pointer items-center rounded-full transition-colors duration-200 disabled:cursor-not-allowed disabled:opacity-50',
                                stepConfirmEnabled ? 'bg-[#159b96]' : 'bg-zinc-300',
                            )}
                        >
                            <span className={cn('inline-block h-3 w-3 rounded-full bg-white shadow-sm transition-transform duration-200', stepConfirmEnabled ? 'translate-x-3.5' : 'translate-x-0.5')} />
                        </button>
                        <span>步进确认</span>
                    </label>
                    {stepConfirmEnabled && (
                        <label className="flex items-center gap-1 text-xs text-indigo-600 select-none">
                            <span>批数</span>
                            <input
                                type="number"
                                min={1}
                                value={stepBatchSize}
                                onChange={e => setStepBatchSize(parseInt(e.target.value) || 1)}
                                className="h-6 w-12 rounded border border-zinc-300 bg-white px-1.5 text-center text-xs text-zinc-900 focus:border-indigo-400 focus:outline-none focus:ring-1 focus:ring-indigo-300"
                            />
                        </label>
                    )}
                    {stepState.active && stepState.processing && (
                        <span className="flex items-center gap-1 text-xs text-indigo-500">
                            <span className="h-3 w-3 animate-spin rounded-full border-2 border-indigo-500 border-t-transparent" />
                            正在处理 {stepState.currentProductIndex + 1}/{stepState.totalProducts} 产品
                        </span>
                    )}
                    {aiEditRunning && (
                        <span className="flex items-center gap-1 text-xs text-indigo-500">
                            <span className="h-3 w-3 animate-spin rounded-full border-2 border-indigo-500 border-t-transparent" />
                            AI 编辑首图 {aiEditProgress}
                        </span>
                    )}
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={handleExecute}
                        disabled={selectedTasks.size === 0 || !hasSelection || stepState.processing || aiEditRunning}
                        className="bg-[#159b96] text-white hover:bg-[#11837f]"
                    >
                        执行
                    </Button>
                    {hasSelection ? (
                        <Button variant="ghost" size="sm" onClick={deselectAll} className="text-xs text-[#159b96]">取消全选</Button>
                    ) : (
                        <Button variant="ghost" size="sm" onClick={selectAll} className="text-xs text-[#159b96]">全选</Button>
                    )}
                </div>
            </div>

            {/* 分组列表 */}
            <div className="flex-1 overflow-auto px-6 py-4"
                onScroll={(event) => {
                    if (sourceMode === 'collection-box') {
                        const shouldCollapse = event.currentTarget.scrollTop > 24;
                        setCollectionFiltersCollapsed(current => current === shouldCollapse ? current : shouldCollapse);
                    }
                }}>
                {groups.length === 0 ? (
                    sourceMode === 'collection-box' ? (
                        <div className="flex min-h-[55vh] flex-col items-center justify-center gap-3 text-zinc-400">
                            {sourceLoading ? <Loader2 size={32} className="animate-spin text-[#159b96]" /> : <Search size={32} />}
                            <span className="text-sm">{sourceLoading ? '正在筛选公共采集箱商品…' : '当前分类没有符合筛选条件的商品'}</span>
                        </div>
                    ) : (appliedSearch.search || appliedSearch.createdFrom || appliedSearch.createdTo || appliedSearch.processedFrom || appliedSearch.processedTo || appliedSearch.flagFilter) ? (
                        <div className="py-16 text-center text-sm text-zinc-400">没有匹配的数据</div>
                    ) : (
                        <div
                            className={cn(
                                'flex flex-col items-center justify-center gap-4 rounded-2xl border-2 border-dashed p-12 transition-all duration-200',
                                dragOver ? 'border-indigo-400 bg-indigo-50/50' : 'border-zinc-200 bg-zinc-50/30 hover:border-zinc-300 hover:bg-zinc-50/50',
                            )}
                            style={{ minHeight: '60vh' }}
                            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                            onDragLeave={() => setDragOver(false)}
                            onDrop={handleDrop}
                        >
                            <input
                                ref={emptyFileInputRef}
                                type="file"
                                accept=".xlsx,.xls"
                                className="hidden"
                                onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFileImport(f); }}
                            />
                            {importing ? (
                                <>
                                    <Loader2 size={40} className="animate-spin text-indigo-400" />
                                    <span className="text-sm font-medium text-indigo-600">正在导入…</span>
                                </>
                            ) : (
                                <>
                                    <div className={cn('flex h-16 w-16 items-center justify-center rounded-2xl transition-colors', dragOver ? 'bg-indigo-100 text-indigo-500' : 'bg-zinc-100 text-zinc-400')}>
                                        <FileSpreadsheet size={32} />
                                    </div>
                                    <div className="text-center">
                                        <p className="text-sm font-medium text-zinc-600">拖拽 Excel 文件到此处，或点击选择文件</p>
                                        <p className="mt-1 text-xs text-zinc-400">支持 .xlsx / .xls 格式</p>
                                    </div>
                                    <Button variant="primary" size="sm" onClick={() => emptyFileInputRef.current?.click()} className="mt-1">
                                        <Upload size={14} /> 选择文件
                                    </Button>
                                    {importError && (
                                        <p className="mt-2 text-xs text-red-500">{importError}</p>
                                    )}
                                </>
                            )}
                        </div>
                    )
                ) : (
                    <div className="flex flex-col gap-0">
                        <div className="grid h-9 shrink-0 grid-cols-[46%_54%] overflow-hidden rounded-t-md bg-[#f5f7f7] text-center text-xs font-medium text-[#159b96]">
                            <div className="relative flex items-center justify-center gap-2 px-5">
                                <button
                                    type="button"
                                    role="checkbox"
                                    aria-checked={allProductsSelected}
                                    onClick={allProductsSelected ? deselectAll : selectAll}
                                    className="absolute left-5 flex items-center gap-1.5"
                                    title="全选所有产品"
                                >
                                    {allProductsSelected ? <CheckSquare size={14} /> : <Square size={14} />}
                                    <span>全选</span>
                                </button>
                                <span>源素材</span>
                            </div>
                            <div className="flex items-center justify-center">AI处理</div>
                        </div>
                        <div className="flex flex-col gap-[5px]">
                        {pagedGroups.map(group => (
                            <GroupCard key={group.productId} group={group}
                                expanded={expandedGroups.has(group.productId)} selected={selectedProducts.has(group.productId)}
                                onToggle={() => toggleGroup(group.productId)} onToggleSelect={() => toggleProductSelect(group.productId)}
                                currentPage={groupPages[group.productId] ?? 1} onPageChange={(p) => setGroupSkuPage(group.productId, p)}
                                commonDisplayFields={commonDisplayFields} skuDisplayFields={skuDisplayFields}
                                lowQualityConfig={lowQualityConfig}
                                imageReplacements={imageReplacements}
                                originalRows={originalRows} cellErrors={cellErrors} onCellClick={openCellEdit} onBatchClick={openBatch}
                                onThumbEnter={handleThumbEnter} onThumbLeave={() => setHoverImage(null)}
                                filteredRowIndices={filteredRowIndices} onUnmarkFiltered={unmarkRowsFiltered}
                                onCompare={() => setCompareProductId(group.productId)}
                                onImageClick={handleImageClick}
                                showCommonEditor={sourceMode === 'collection-box'}
                                onOpenCommonEditor={openCommonItemEditor}
                                flag={productFlags[group.productId] ?? DEFAULT_FLAG}
                                onSetFlag={(c) => setProductFlag([group.productId], c)} />
                        ))}
                        </div>
                    </div>
                )}
            </div>

            {/* 底部固定分页栏 */}
            {(sourceMode === 'collection-box' ? sourcePagination.total > 0 : groups.length > 0) && (
                <div className="flex shrink-0 items-center justify-between border-t border-zinc-200 bg-white px-4 py-2">
                    <div className="flex items-center gap-3">
                        <span className="text-xs text-zinc-500">
                            第 <span className="font-semibold text-zinc-800">{sourceMode === 'collection-box' ? `${sourcePagination.from}-${sourcePagination.to}` : `${(safeGroupPage - 1) * groupPageSize + 1}-${Math.min(safeGroupPage * groupPageSize, groups.length)}`}</span> 个产品 / 共 <span className="font-semibold text-zinc-800">{sourceMode === 'collection-box' ? sourcePagination.total : groups.length}</span> 个
                        </span>
                        <div className="flex items-center gap-1.5">
                            <span className="text-xs text-zinc-400">每页</span>
                            <select
                                value={sourceMode === 'collection-box' ? sourcePagination.per_page : groupPageSize}
                                onChange={(e) => {
                                    const size = Number(e.target.value);
                                    if (sourceMode === 'collection-box') loadCollectionPage(1, size);
                                    else { setGroupPageSize(size); setGroupPage(1); }
                                }}
                                className="rounded border border-zinc-300 px-1.5 py-0.5 text-xs text-zinc-700"
                            >
                                {(sourceMode === 'collection-box' ? [10, 20, 30, 50, 100, 200, 500] : [5, 10, 20, 50]).map(n => <option key={n} value={n}>{n}</option>)}
                            </select>
                            <span className="text-xs text-zinc-400">条</span>
                        </div>
                    </div>
                    <div className="flex items-center gap-1">
                        <button onClick={() => sourceMode === 'collection-box' ? loadCollectionPage(1) : setGroupPage(1)} disabled={safeGroupPage <= 1 || sourceLoading} className="rounded p-1 transition-colors hover:bg-zinc-100 hover:text-zinc-800 disabled:opacity-30"><ChevronsLeft size={16} /></button>
                        <button onClick={() => sourceMode === 'collection-box' ? loadCollectionPage(Math.max(1, safeGroupPage - 1)) : setGroupPage(Math.max(1, safeGroupPage - 1))} disabled={safeGroupPage <= 1 || sourceLoading} className="rounded p-1 transition-colors hover:bg-zinc-100 hover:text-zinc-800 disabled:opacity-30"><ChevronLeft size={16} /></button>
                        <span className="min-w-[80px] text-center text-sm font-medium text-zinc-700">{safeGroupPage} / {totalGroupPages}</span>
                        <button onClick={() => sourceMode === 'collection-box' ? loadCollectionPage(Math.min(totalGroupPages, safeGroupPage + 1)) : setGroupPage(Math.min(totalGroupPages, safeGroupPage + 1))} disabled={safeGroupPage >= totalGroupPages || sourceLoading} className="rounded p-1 transition-colors hover:bg-zinc-100 hover:text-zinc-800 disabled:opacity-30"><ChevronRightIcon size={16} /></button>
                        <button onClick={() => sourceMode === 'collection-box' ? loadCollectionPage(totalGroupPages) : setGroupPage(totalGroupPages)} disabled={safeGroupPage >= totalGroupPages || sourceLoading} className="rounded p-1 transition-colors hover:bg-zinc-100 hover:text-zinc-800 disabled:opacity-30"><ChevronsRight size={16} /></button>
                        <div className="ml-2 flex items-center gap-1">
                            <span className="text-xs text-zinc-400">跳至</span>
                            <input
                                type="number"
                                min={1}
                                max={totalGroupPages}
                                value={jumpPage}
                                onChange={(e) => setJumpPage(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        const p = parseInt(jumpPage, 10);
                                        if (p >= 1 && p <= totalGroupPages) {
                                            if (sourceMode === 'collection-box') loadCollectionPage(p);
                                            else setGroupPage(p);
                                        }
                                        setJumpPage('');
                                    }
                                }}
                                className="w-12 rounded border border-zinc-300 px-1.5 py-0.5 text-center text-xs text-zinc-700"
                                placeholder={String(safeGroupPage)}
                            />
                            <span className="text-xs text-zinc-400">页</span>
                        </div>
                    </div>
                </div>
            )}

            {hoverImage && (
                <div className="pointer-events-none fixed z-[100] animate-fadeIn" style={{ left: hoverImage.left, top: hoverImage.top }}>
                    {hoverImage.originalUrl ? (
                        <div className="flex gap-2 rounded-lg border border-zinc-200 bg-white p-1.5 shadow-xl">
                            <div className="flex flex-col items-center gap-0.5">
                                <span className="text-[10px] font-medium text-zinc-400">原图</span>
                                <img src={hoverImage.originalUrl} alt="" className="h-[240px] w-[240px] rounded-md border border-zinc-100 object-contain bg-zinc-50" onError={() => setHoverImage(null)} />
                            </div>
                            <div className="flex flex-col items-center gap-0.5">
                                <span className="text-[10px] font-medium text-amber-500">修改后</span>
                                <img src={hoverImage.url} alt="" className="h-[240px] w-[240px] rounded-md border border-amber-200 object-contain bg-zinc-50" onError={() => setHoverImage(null)} />
                            </div>
                        </div>
                    ) : (
                        <img src={hoverImage.url} alt="" className="h-[280px] w-[280px] rounded-lg border border-zinc-200 bg-white object-contain shadow-xl" onError={() => setHoverImage(null)} />
                    )}
                </div>
            )}

            <CellEditModal open={!!cellEdit} onClose={() => setCellEdit(null)} field={cellEdit?.field ?? null} rowIndex={cellEdit?.rowIndex ?? -1} currentValue={cellEdit?.currentValue ?? ''} originalValue={cellEdit?.originalValue ?? ''} rowNumber={cellEdit?.rowNumber ?? 0} onSave={handleCellSave} />
            <CompareModal open={compareOpen} onClose={() => setCompareOpen(false)} />
            <ProductCompareModal productId={compareProductId} open={!!compareProductId} onClose={() => setCompareProductId(null)} onAiTitle={runAiTitleForProduct} onAiImage={runAiImageForProduct} />
            <StepConfirmModal open={stepState.active && !stepState.processing && stepState.completedResults.length > 0} />

            <Modal open={!!recoveryDraft} title="检测到未完成的 AI 任务" width="sm">
                <div className="text-sm text-zinc-600">
                    已恢复处理结果、勾选范围和进度（{recoveryDraft?.progress.completed ?? 0}/{recoveryDraft?.progress.total ?? 0}）。
                    继续后会从中断商品开始，已完成商品不会重复处理。
                </div>
                <div className="mt-6 flex justify-end gap-2">
                    <Button variant="outline" size="sm" onClick={() => void abandonRecoveredTask()}>放弃任务</Button>
                    <Button variant="primary" size="sm" onClick={() => void continueRecoveredTask()}>继续处理</Button>
                </div>
            </Modal>

            <ImageEditorModal
                open={editorOpen}
                onClose={() => setEditorOpen(false)}
                imageUrl={editorImageUrl}
                readOnly={editorReadOnly}
                onSave={editorOnSaveRef.current ?? undefined}
            />

            <ConfirmDialog
                open={saveConfirmOpen}
                title="确认保存 AI 修改"
                message={`勾选的商品中有 ${Array.from(filteredRowIndices).filter(index => new Set(selectedRowIndices).has(index)).length} 个 SKU 行被过滤，保存后将从公共采集箱商品规格中删除。是否继续？`}
                confirmText="保存并删除"
                cancelText="取消"
                onConfirm={() => void performCollectionBoxSave()}
                onCancel={() => setSaveConfirmOpen(false)}
            />

            <Modal
                open={!!saveResult || !!saveError}
                onClose={() => { setSaveResult(null); setSaveError(''); }}
                title="AI 修改保存结果"
                width="lg"
            >
                {saveError ? (
                    <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{saveError}</div>
                ) : saveResult ? (
                    <div className="flex flex-col gap-4">
                        <div className="grid grid-cols-3 gap-3">
                            <div className="rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-700">成功：{saveResult.success_count}</div>
                            <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">失败：{saveResult.failed_count}</div>
                            <div className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-700">剩余：{saveResult.remaining_count}</div>
                        </div>
                        {saveResult.errors.length > 0 && (
                            <>
                                <div className="flex items-center justify-between">
                                    <span className="text-sm font-medium text-zinc-700">完整错误列表</span>
                                    <Button variant="outline" size="sm" onClick={() => void navigator.clipboard.writeText(saveResult.errors.map(error => `商品 ${error.item_id}: ${error.message}`).join('\n'))}>复制错误</Button>
                                </div>
                                <div className="max-h-72 overflow-auto rounded-md border border-red-200 bg-red-50 p-3 text-xs leading-5 text-red-700 whitespace-pre-wrap">
                                    {saveResult.errors.map(error => `商品 ${error.item_id}: ${error.message}`).join('\n')}
                                </div>
                            </>
                        )}
                    </div>
                ) : null}
            </Modal>

            <Modal open={!!batch} onClose={() => setBatch(null)} title={batch ? `批量操作 · ${batch.field.label}` : ''} width="md">
                {batch && (
                    <div>
                        <div className="mb-4 inline-flex rounded-lg border border-zinc-200 p-0.5">
                            <button onClick={() => setBatch({ ...batch, mode: 'replace' })} className={cn('rounded-md px-3 py-1 text-sm transition-colors', batch.mode === 'replace' ? 'bg-indigo-600 text-white' : 'text-zinc-600 hover:bg-zinc-100')}>查找替换</button>
                            <button onClick={() => setBatch({ ...batch, mode: 'set' })} className={cn('rounded-md px-3 py-1 text-sm transition-colors', batch.mode === 'set' ? 'bg-indigo-600 text-white' : 'text-zinc-600 hover:bg-zinc-100')}>批量设置</button>
                        </div>
                        {batch.mode === 'replace' ? (
                            <div className="flex flex-col gap-3"><Input label="查找内容" value={findValue} onChange={(e) => setFindValue(e.target.value)} placeholder="输入要查找的文本" /><Input label="替换为" value={replaceValue} onChange={(e) => setReplaceValue(e.target.value)} placeholder="输入替换后的文本" /><p className="text-xs text-zinc-400">将对 {batch.selectedRowIndices.length} 行执行替换。</p></div>
                        ) : (
                            <div className="flex flex-col gap-3"><Textarea label="设置值" value={setValue} onChange={(e) => setSetValue(e.target.value)} placeholder="对选中行设置相同值" /><p className="text-xs text-zinc-400">将对 {batch.selectedRowIndices.length} 行设置为此值。</p></div>
                        )}
                        <div className="mt-5 flex justify-end gap-2"><Button variant="outline" size="sm" onClick={() => setBatch(null)}>取消</Button><Button variant="primary" size="sm" onClick={applyBatch}>应用</Button></div>
                    </div>
                )}
            </Modal>
        </div>
    );
}

// --- 列设置下拉（含拖拽排序 + 可见性） ---

function ColumnSettingsDropdown({ fields, hiddenFields, fieldOrder, onToggle, onReset, onReorder, onResetOrder, onClose }: {
    fields: ExcelField[];
    hiddenFields: string[];
    fieldOrder: string[];
    onToggle: (k: string) => void;
    onReset: () => void;
    onReorder: (order: string[]) => void;
    onResetOrder: () => void;
    onClose: () => void;
}) {
    const ref = useRef<HTMLDivElement>(null);
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    useEffect(() => {
        const handler = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) onClose(); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [onClose]);

    // 排序后的字段列表
    const orderedFields = useMemo(() => {
        if (fieldOrder.length === 0) return fields;
        const ordered = fieldOrder.map(key => fields.find(f => f.key === key)).filter((f): f is ExcelField => !!f);
        const rest = fields.filter(f => !fieldOrder.includes(f.key));
        return [...ordered, ...rest];
    }, [fields, fieldOrder]);

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const oldIndex = orderedFields.findIndex(f => f.key === active.id);
        const newIndex = orderedFields.findIndex(f => f.key === over.id);
        if (oldIndex < 0 || newIndex < 0) return;
        const newOrder = arrayMove(orderedFields, oldIndex, newIndex).map(f => f.key);
        onReorder(newOrder);
    };

    return (
        <div ref={ref} className="absolute left-0 top-full z-[300] mt-1 w-64 rounded-xl border border-zinc-200 bg-white shadow-xl animate-fadeIn">
            <div className="flex items-center justify-between border-b border-zinc-200 px-3 py-2">
                <span className="text-xs font-semibold text-zinc-700">列设置</span>
                <div className="flex gap-2">
                    <button onClick={onResetOrder} className="text-xs text-zinc-400 hover:text-zinc-600">重置顺序</button>
                    {hiddenFields.length > 0 && <button onClick={onReset} className="text-xs text-indigo-500 hover:text-indigo-700">显示全部</button>}
                </div>
            </div>
            <div className="max-h-72 overflow-y-auto p-1">
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext items={orderedFields.map(f => f.key)} strategy={verticalListSortingStrategy}>
                        {orderedFields.map(field => (
                            <SortableFieldItem key={field.key} field={field} isHidden={hiddenFields.includes(field.key)} onToggle={onToggle} />
                        ))}
                    </SortableContext>
                </DndContext>
            </div>
            <div className="border-t border-zinc-100 px-3 py-1.5 text-center text-[10px] text-zinc-400">拖动 ⠿ 调整顺序 · 点击眼睛切换显示</div>
        </div>
    );
}

function SortableFieldItem({ field, isHidden, onToggle }: { field: ExcelField; isHidden: boolean; onToggle: (k: string) => void }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: field.key });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 10 : 'auto',
    };
    return (
        <div ref={setNodeRef} style={style}
            className={cn('flex items-center gap-2 rounded-lg px-2 py-1.5 transition-colors', isDragging ? 'bg-indigo-50 shadow-md' : 'hover:bg-zinc-50')}>
            <button {...attributes} {...listeners} className="cursor-grab text-zinc-300 hover:text-zinc-500 active:cursor-grabbing">
                <GripVertical size={14} />
            </button>
            <button onClick={() => onToggle(field.key)} className="flex flex-1 items-center gap-2 text-left min-w-0">
                {isHidden ? <EyeOff size={14} className="shrink-0 text-zinc-300" /> : <Eye size={14} className="shrink-0 text-indigo-500" />}
                <span className={cn('truncate text-sm', isHidden ? 'text-zinc-400' : 'text-zinc-800')}>{field.label}</span>
            </button>
        </div>
    );
}

// 旗子图标（按颜色着色）
function FlagIcon({ color, size = 16 }: { color: FlagColor; size?: number }) {
    const opt = FLAG_OPTIONS.find(o => o.color === color) ?? FLAG_OPTIONS[0];
    return <Flag size={size} className={opt.textClass} fill="currentColor" />;
}

// 旗子选择器：点击触发弹出颜色面板；可选是否提供「清除/全部」项（用于筛选）
function FlagSelector({
    value, onSelect, onClear, clearLabel = '清除', title, triggerClassName,
}: {
    value: FlagColor;
    onSelect: (c: FlagColor) => void;
    onClear?: () => void;
    clearLabel?: string;
    title?: string;
    triggerClassName?: string;
}) {
    const [open, setOpen] = useState(false);
    const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const popRef = useRef<HTMLDivElement>(null);

    const openMenu = () => {
        const rect = triggerRef.current?.getBoundingClientRect();
        if (!rect) return;
        const popW = 144; // w-36
        const popH = onClear ? 150 : 130;
        let left = rect.left;
        let top = rect.bottom + 6;
        if (left + popW > window.innerWidth - 8) left = window.innerWidth - popW - 8;
        if (top + popH > window.innerHeight - 8) top = Math.max(8, rect.top - popH - 6);
        setPos({ left: Math.max(8, left), top: Math.max(8, top) });
        setOpen(true);
    };

    useEffect(() => {
        if (!open) return;
        const handler = (e: MouseEvent) => {
            if (triggerRef.current && triggerRef.current.contains(e.target as Node)) return;
            if (popRef.current && popRef.current.contains(e.target as Node)) return;
            setOpen(false);
        };
        const onScroll = () => setOpen(false);
        document.addEventListener('mousedown', handler);
        window.addEventListener('scroll', onScroll, true);
        window.addEventListener('resize', onScroll);
        return () => {
            document.removeEventListener('mousedown', handler);
            window.removeEventListener('scroll', onScroll, true);
            window.removeEventListener('resize', onScroll);
        };
    }, [open]);

    return (
        <>
            <button
                ref={triggerRef}
                type="button"
                onClick={() => (open ? setOpen(false) : openMenu())}
                title={title ?? '选择旗子'}
                className={cn('flex h-7 w-7 items-center justify-center rounded-md border border-zinc-200 bg-white transition-colors hover:bg-zinc-50', triggerClassName)}
            >
                <FlagIcon color={value} size={16} />
            </button>
            {open && pos && createPortal(
                <div
                    ref={popRef}
                    style={{ left: pos.left, top: pos.top }}
                    className="fixed z-[200] w-36 rounded-lg border border-zinc-200 bg-white p-1 shadow-xl animate-fadeIn"
                >
                    <div className="grid grid-cols-4 gap-0.5 p-0.5">
                        {FLAG_OPTIONS.map(opt => (
                            <button
                                key={opt.color}
                                type="button"
                                title={opt.label}
                                onClick={() => { onSelect(opt.color); setOpen(false); }}
                                className={cn(
                                    'flex h-8 w-8 items-center justify-center rounded-md transition-colors hover:bg-zinc-100',
                                    value === opt.color && 'ring-2 ring-indigo-300',
                                )}
                            >
                                <FlagIcon color={opt.color} size={18} />
                            </button>
                        ))}
                    </div>
                    {onClear && (
                        <button
                            type="button"
                            onClick={() => { onClear(); setOpen(false); }}
                            className="mt-1 w-full rounded-md border-t border-zinc-100 px-2 py-1.5 text-center text-xs text-zinc-500 hover:bg-zinc-50"
                        >
                            {clearLabel}
                        </button>
                    )}
                </div>,
                document.body,
            )}
        </>
    );
}

// --- 分组卡片 ---

interface GroupCardProps {
    group: Group; expanded: boolean; selected: boolean;
    onToggle: () => void; onToggleSelect: () => void;
    currentPage: number; onPageChange: (page: number) => void;
    commonDisplayFields: ExcelField[]; skuDisplayFields: ExcelField[];
    lowQualityConfig: Pick<LowQualityFilterConfig, 'filterDetailImages' | 'filterMainImages' | 'filterSkuImages'>;
    imageReplacements: Map<string, string>;
    originalRows: DataRow[]; cellErrors: Record<number, Record<string, string>>;
    onCellClick: (rowIndex: number, field: ExcelField) => void;
    onBatchClick: (field: ExcelField) => void;
    onThumbEnter: (e: React.MouseEvent, url: string) => void; onThumbLeave: () => void;
    filteredRowIndices: Set<number>; onUnmarkFiltered: (indices: number[]) => void;
    onCompare: () => void;
    onImageClick: (url: string, onSave: (newUrl: string) => void) => void;
    showCommonEditor: boolean; onOpenCommonEditor: (itemId: string) => void;
    flag: FlagColor; onSetFlag: (color: FlagColor) => void;
}

function GroupCard({ group, expanded, selected, onToggle, onToggleSelect, currentPage, onPageChange,
    commonDisplayFields, skuDisplayFields, originalRows, cellErrors, onCellClick, onBatchClick, onThumbEnter, onThumbLeave,
    filteredRowIndices, onUnmarkFiltered, onCompare, onImageClick, showCommonEditor, onOpenCommonEditor, flag, onSetFlag, lowQualityConfig, imageReplacements }: GroupCardProps) {
    const { updateCell } = useDataStore();
    const total = group.rows.length;
    const totalPages = Math.max(1, Math.ceil(total / SKU_PAGE_SIZE));
    const safePage = Math.min(currentPage, totalPages);
    const pageStart = (safePage - 1) * SKU_PAGE_SIZE;
    const pageEnd = Math.min(pageStart + SKU_PAGE_SIZE, total);
    const pagedRows = group.rows.slice(pageStart, pageEnd);

    const firstRow = group.rows[0]?.row;
    const firstOrigRow = originalRows[group.rows[0]?.index ?? 0];
    const firstRowIndex = group.rows[0]?.index ?? 0;
    const commonItemId = String(firstRow?.__commonItemId ?? '');
    const productId = group.productId;
    const productName = firstRow?.productName ?? '';
    const originalProductName = firstOrigRow?.productName ?? '';
    const isProductNameModified = productName !== originalProductName;
    const productError = cellErrors[firstRowIndex]?.productName;
    const firstImage = firstRow?.firstImage ?? parseImageUrls(firstRow?.mainImages ?? '')[0] ?? '';
    const originalFirstImage = firstOrigRow?.firstImage ?? parseImageUrls(firstOrigRow?.mainImages ?? '')[0] ?? '';
    const collectionGroupNames = Array.from(new Set(String(firstRow?.collectionGroupNames ?? '')
        .split('\n')
        .map(name => name.trim())
        .filter(Boolean)));
    const visibleCollectionGroupNames = collectionGroupNames.slice(0, 3);

    // 计算该产品组内所有被修改过的字段（任一 SKU 行与原值不同即计入）
    const modifiedFieldKeys = useMemo(() => {
        const keys = new Set<string>();
        const fields = [...commonDisplayFields, ...skuDisplayFields];
        for (const { row, index } of group.rows) {
            const orig = originalRows[index];
            if (!orig) { fields.forEach(f => keys.add(f.key)); continue; }
            for (const f of fields) {
                if ((row[f.key] ?? '') !== (orig[f.key] ?? '')) keys.add(f.key);
            }
        }
        return keys;
    }, [group.rows, commonDisplayFields, skuDisplayFields, originalRows]);

    // 按显示顺序排列的已修改字段（含原值/新值及图片字段的代表图，用于 hover 对比）
    const modifiedFields = useMemo(() => {
        const order = [...commonDisplayFields, ...skuDisplayFields];
        return order
            .filter(f => modifiedFieldKeys.has(f.key))
            .map(f => {
                const value = firstRow?.[f.key] ?? '';
                const origValue = firstOrigRow?.[f.key] ?? '';
                const imageUrl = f.isImage ? parseImageUrls(value)[0] : undefined;
                const origImageUrl = f.isImage ? parseImageUrls(origValue)[0] : undefined;
                // SKU 级字段（如 SKU 规格、SKU 估重）不聚合，收集每个 SKU 行的前后对比列表
                let rows: { skuName: string; originalValue: string; value: string }[] | undefined;
                if (SKU_SPECIFIC_FIELDS.has(f.key)) {
                    const list = group.rows
                        .map(({ row, index }) => {
                            const orig = originalRows[index] ?? {};
                            const rawVal = row[f.key] ?? '';
                            const rawOrig = orig[f.key] ?? '';
                            if (rawVal === rawOrig) return null;
                            const skuName = buildSkuName(row, index);
                            const imageUrl = parseImageUrls(String(row.skuImage ?? ''))[0] || parseImageUrls(String(row.firstImage ?? ''))[0] || undefined;
                            return { skuName, originalValue: String(rawOrig), value: String(rawVal), imageUrl };
                        })
                        .filter((x): x is { skuName: string; originalValue: string; value: string; imageUrl: string | undefined } => x !== null);
                    if (list.length >= 1) rows = list;
                }
                return {
                    key: f.key, label: f.label.replace(/^\*?/, ''), isImage: f.isImage,
                    imageUrl, originalImageUrl: origImageUrl, value, originalValue: origValue, rows,
                };
            });
    }, [modifiedFieldKeys, commonDisplayFields, skuDisplayFields, firstRow, firstOrigRow, group.rows, originalRows]);
    const restoreFilteredImages = useCallback((images: FilteredImageInfo[]) => {
        const grouped = new Map<string, { rowIndex: number; fieldKey: FilteredImageInfo['fieldKey']; urls: Set<string> }>();
        const restoredSkuRows = new Set<number>();
        for (const image of images) {
            for (const rowIndex of image.rowIndices) {
                const key = `${rowIndex}\u0000${image.fieldKey}`;
                const entry = grouped.get(key) ?? { rowIndex, fieldKey: image.fieldKey, urls: new Set<string>() };
                entry.urls.add(image.url);
                grouped.set(key, entry);
                if (image.fieldKey === 'skuImage') restoredSkuRows.add(rowIndex);
            }
        }

        for (const { rowIndex, fieldKey, urls } of grouped.values()) {
            const currentRow = useDataStore.getState().rows[rowIndex];
            const originalRow = originalRows[rowIndex];
            if (!currentRow || !originalRow) continue;
            const currentUrls = parseImageUrls(String(currentRow[fieldKey] ?? ''));
            const originalUrls = parseImageUrls(String(originalRow[fieldKey] ?? ''));
            const currentSet = new Set(currentUrls);
            const merged = originalUrls.filter(url => currentSet.has(url) || urls.has(url));
            for (const url of currentUrls) {
                if (!merged.includes(url)) merged.push(url);
            }
            updateCell(rowIndex, fieldKey, joinImageUrls(merged));
        }

        // SKU 图片被恢复后，该行不再属于“低质量过滤”，同步移除过滤标记。
        if (restoredSkuRows.size > 0) onUnmarkFiltered(Array.from(restoredSkuRows));
    }, [originalRows, updateCell, onUnmarkFiltered]);

    const filteredImageItems = useMemo<ModifiedBadgeItem[]>(() => {
        const collectRemoved = (fieldKey: 'mainImages' | 'detailImages' | 'skuImage') => {
            const removed: FilteredImageInfo[] = [];
            const collected = new Map<string, FilteredImageInfo>();
            for (const { row, index } of group.rows) {
                const original = originalRows[index];
                if (!original) continue;
                const currentUrls = new Set(parseImageUrls(String(row[fieldKey] ?? '')));
                for (const url of parseImageUrls(String(original[fieldKey] ?? ''))) {
                    // 图生图是替换而非过滤，不应出现在“被过滤图片”中。
                    const replacement = imageReplacements.get(url);
                    if (currentUrls.has(url) || (replacement && currentUrls.has(replacement))) continue;
                    const skuName = fieldKey === 'skuImage' ? buildSkuName(row, index) : undefined;
                    const identity = `${url}\u0000${skuName ?? ''}`;
                    const existing = collected.get(identity);
                    if (existing) {
                        existing.rowIndices.push(index);
                        continue;
                    }
                    const image = { url, skuName, rowIndices: [index], fieldKey };
                    collected.set(identity, image);
                    removed.push(image);
                }
            }
            return removed;
        };

        const definitions: Array<{
            enabled: boolean;
            fieldKey: 'mainImages' | 'detailImages' | 'skuImage';
            key: string;
            label: string;
        }> = [
            { enabled: lowQualityConfig.filterMainImages, fieldKey: 'mainImages', key: 'filter-mainImages', label: '过滤主图' },
            { enabled: lowQualityConfig.filterDetailImages, fieldKey: 'detailImages', key: 'filter-detailImages', label: '过滤详情图' },
            { enabled: lowQualityConfig.filterSkuImages, fieldKey: 'skuImage', key: 'filter-skuImage', label: '过滤 SKU 图' },
        ];

        return definitions.flatMap(({ enabled, fieldKey, key, label }) => {
            if (!enabled) return [];
            const filteredImages = collectRemoved(fieldKey);
            if (filteredImages.length === 0) return [];
            return [{
                key,
                label,
                isImage: false,
                imageUrl: filteredImages[0]?.url,
                value: `已过滤 ${filteredImages.length} 张`,
                originalValue: '',
                filteredImages,
                onRestoreFiltered: restoreFilteredImages,
            }];
        });
    }, [group.rows, originalRows, imageReplacements, lowQualityConfig, restoreFilteredImages]);

    const filterSourceKeys = useMemo<Set<string>>(() => new Set(filteredImageItems.map(item => {
        if (item.key === 'filter-mainImages') return 'mainImages';
        if (item.key === 'filter-detailImages') return 'detailImages';
        return 'skuImage';
    })), [filteredImageItems]);
    const aiEditedFirstImage = Boolean(firstImage) && Array.from(imageReplacements.values()).includes(firstImage);

    // 产品名称已作为上方独立行展示。图生图保留“首图”；过滤操作只显示实际被删除的图片。
    const badgeFields = useMemo(() => modifiedFields.filter(field => {
        if (field.key === 'productName' || filterSourceKeys.has(field.key)) return false;
        if (field.key === 'firstImage') {
            if (aiEditedFirstImage) return true;
            if (filterSourceKeys.has('mainImages')) return false;
        }
        if (field.key === 'mainImages' && aiEditedFirstImage) return false;
        return !(field.key === 'mainImages' && modifiedFieldKeys.has('firstImage'));
    }), [modifiedFields, modifiedFieldKeys, filterSourceKeys, aiEditedFirstImage]);
    const filteredRows = useMemo(() => group.rows
        .filter(({ index }) => filteredRowIndices.has(index))
        .map(({ row, index }) => ({
            skuName: buildSkuName(row, index),
            originalValue: '保留',
            value: '已过滤',
            imageUrl: parseImageUrls(String(row.skuImage ?? ''))[0] || firstImage || undefined,
        })), [group.rows, filteredRowIndices, firstImage]);
    const resultFields = useMemo<ModifiedBadgeItem[]>(() => {
        const items: ModifiedBadgeItem[] = [...badgeFields, ...filteredImageItems];
        if (filteredRows.length === 0 || filteredImageItems.some(field => field.key === 'filter-skuImage')) return items;
        return [...items, {
            key: '__filtered__',
            label: '过滤低质量',
            isImage: false,
            value: `${filteredRows.length} 个 SKU`,
            originalValue: '',
            rows: filteredRows,
        }];
    }, [badgeFields, filteredImageItems, filteredRows]);

    return (
        <div className={cn(
            'overflow-hidden rounded-lg border bg-white transition-colors duration-200',
            selected ? 'border-[#159b96] bg-[#fbfefe]' : 'border-[#e1e8ea]',
        )}>
            {/* 分组头 */}
            <div className="grid min-h-[132px] grid-cols-[46%_54%] text-left">
                <div className="flex min-w-0 items-center gap-3 px-5 py-3">
                    <button onClick={(e) => { e.stopPropagation(); onToggleSelect(); }}
                        className={cn('shrink-0 p-0.5 transition-colors', selected ? 'text-[#159b96]' : 'text-zinc-400 hover:text-[#159b96]')} title={selected ? '取消选中' : '选中此产品'}>
                        {selected ? <CheckSquare size={18} /> : <Square size={18} />}
                    </button>
                    <FlagSelector value={flag} onSelect={onSetFlag} title="设置此产品的旗子" triggerClassName="h-8 w-8" />
                    {originalFirstImage ? (
                        <img src={originalFirstImage} alt="" className="h-16 w-16 shrink-0 cursor-pointer border border-[#d8e2e4] object-contain"
                            onMouseEnter={(e) => onThumbEnter(e, originalFirstImage)} onMouseLeave={onThumbLeave}
                            onClick={(e) => { e.stopPropagation(); onImageClick(originalFirstImage, (newUrl) => { updateCell(firstRowIndex, 'firstImage', newUrl); }); }} />
                    ) : (
                        <div className="flex h-16 w-16 shrink-0 items-center justify-center border border-zinc-200 bg-zinc-50"><ImageOff size={18} className="text-zinc-300" /></div>
                    )}
                    <div className="min-w-0 flex-1">
                        <p className="line-clamp-2 text-xs font-semibold leading-5 text-[#26343a]">{originalProductName || '未命名产品'}</p>
                        <p className="mt-1 text-[11px] text-[#909b9f]">ID：{productId}</p>
                        <p className="mt-2 text-[11px] font-medium text-[#30393d]">货源：<span className="text-[#159b96]">{firstOrigRow?.sourcePlatform || '未知'}</span>　·　{total} 个 SKU</p>
                        <p className="mt-1 text-[11px] text-[#909b9f]">创建时间：{firstOrigRow?.createdAt || firstRow?.createdAt || '—'}</p>
                        <p className="mt-1 text-[11px] text-[#909b9f]">处理时间：{firstRow?.processedAt || '—'}</p>
                        {!expanded && productError && <p className="mt-1 truncate text-xs text-red-500">{productError}</p>}
                    </div>
                    <div
                        className="flex w-28 shrink-0 flex-wrap content-center gap-1"
                        title={collectionGroupNames.length > 0 ? collectionGroupNames.join('\n') : '未分组'}
                    >
                        {visibleCollectionGroupNames.length > 0 ? visibleCollectionGroupNames.map(name => (
                            <span key={name} className="max-w-full truncate rounded bg-[#e7f5f3] px-2 py-1 text-[11px] font-medium text-[#168b86]">{name}</span>
                        )) : (
                            <span className="text-[11px] text-zinc-400">未分组</span>
                        )}
                        {collectionGroupNames.length > visibleCollectionGroupNames.length && (
                            <span className="rounded bg-zinc-100 px-2 py-1 text-[11px] text-zinc-500">+{collectionGroupNames.length - visibleCollectionGroupNames.length}</span>
                        )}
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-2">
                        {showCommonEditor && commonItemId && (
                            <button onClick={(event) => { event.stopPropagation(); onOpenCommonEditor(commonItemId); }}
                                title="打开完整公共商品编辑；如有未保存的 AI 修改，请先保存到公共采集箱"
                                className="px-2 text-xs font-semibold text-[#159b96] hover:underline">商品编辑</button>
                        )}
                        <button onClick={onToggle} className="px-2 text-xs font-semibold text-[#159b96]">{expanded ? '收起' : '展开'}</button>
                    </div>
                </div>
                <div className="flex min-w-0 items-center gap-4 px-5 py-3">
                    {(isProductNameModified || resultFields.length > 0) && (
                        <>
                            <div className="min-w-0 flex-1">
                                {isProductNameModified && <p className="mb-2 truncate text-xs font-semibold text-[#159b96]">{productName}</p>}
                                {resultFields.length > 0 && <ModifiedFieldBadges items={resultFields} />}
                            </div>
                            <button onClick={(e) => { e.stopPropagation(); onCompare(); }}
                                className="inline-flex shrink-0 items-center gap-1 border border-[#d7e2e4] px-2 py-1 text-xs text-[#6c7a80] hover:border-[#8dd4d1] hover:text-[#159b96]"
                                title="查看此产品的前后对比"><GitCompare size={13} /> 对比</button>
                        </>
                    )}
                </div>
            </div>

            {expanded && (
                <div className="animate-fadeIn border-t border-zinc-200/80">
                    {/* 产品公共字段 */}
                    {commonDisplayFields.length > 0 && (
                        <div className="grid grid-cols-2 gap-x-6 gap-y-2.5 border-b border-zinc-200/80 bg-gradient-to-r from-zinc-50/80 to-white px-4 py-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                            {commonDisplayFields.map(field => {
                                const value = firstRow?.[field.key] ?? '';
                                return (
                                    <div key={field.key} className="min-w-0">
                                        <span className="text-[10px] font-medium uppercase tracking-wider text-zinc-400">{field.label}</span>
                                        <div className="cursor-pointer text-sm text-zinc-800 transition-colors hover:text-indigo-600"
                                            onClick={() => onCellClick(group.rows[0]!.index, field)} title={value || undefined}>
                                            {field.isImage ? (
                                                value ? <InlineImageCell value={value} onHoverEnter={onThumbEnter} onHoverLeave={onThumbLeave} onImageClick={(url) => onImageClick(url, (newUrl) => updateCell(firstRowIndex, field.key, newUrl))} /> : <span className="text-zinc-300">—</span>
                                            ) : value ? (
                                                <span className="line-clamp-2 leading-relaxed">{value}</span>
                                            ) : (
                                                <span className="text-zinc-300">—</span>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* SKU 表格 */}
                    {skuDisplayFields.length > 0 && (
                        <div className="overflow-x-auto">
                            <table className="w-full border-separate border-spacing-0 text-sm">
                                <thead>
                                    <tr>
                                        <th className="sticky left-0 top-0 z-30 w-10 border-b border-r border-zinc-200 bg-zinc-50 px-2 py-2 text-center text-xs font-semibold text-zinc-500">#</th>
                                        {skuDisplayFields.map((field) => (
                                            <th key={field.key} className="sticky top-0 z-20 border-b border-r border-zinc-200 bg-zinc-50 px-3 py-2 text-left text-xs font-semibold text-zinc-600">
                                                <div className="flex items-center justify-between gap-1">
                                                    <span className="whitespace-nowrap" title={field.label}>{field.label}</span>
                                                    <button onClick={() => onBatchClick(field)} className="rounded p-0.5 text-zinc-400 hover:bg-zinc-200 hover:text-indigo-600" title="批量操作"><Settings2 size={12} /></button>
                                                </div>
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {pagedRows.map(({ row, index }, i) => {
                                        const orig = originalRows[index];
                                        const isFiltered = filteredRowIndices.has(index);
                                        const rowEdited = !isFiltered && skuDisplayFields.some(f => (row[f.key] ?? '') !== (orig?.[f.key] ?? ''));
                                        return (
                                            <tr key={index} className={cn("group transition-colors hover:bg-indigo-50/30", isFiltered && "bg-zinc-50 hover:bg-zinc-100/50", rowEdited && "text-xs")}>
                                                <td className={cn("sticky left-0 z-10 w-10 border-b border-r border-zinc-200 bg-white px-2 py-1.5 text-center text-xs text-zinc-400 group-hover:bg-indigo-50/50", isFiltered && "bg-zinc-50 line-through text-zinc-300")}>
                                                    {isFiltered ? (
                                                        <button onClick={() => onUnmarkFiltered([index])} className="hover:text-indigo-500" title="取消过滤标记">{pageStart + i + 1}</button>
                                                    ) : pageStart + i + 1}
                                                </td>
                                                {skuDisplayFields.map((field) => {
                                                    const value = row[field.key] ?? '';
                                                    const origValue = orig?.[field.key] ?? '';
                                                    const isEdited = value !== origValue;
                                                    return (
                                                        <td key={field.key}
                                                            className={cn('cursor-pointer border-b border-r border-zinc-200 px-3 py-1.5 align-top transition-colors duration-150 hover:bg-indigo-50/50',
                                                                isFiltered ? 'text-zinc-400 line-through' : 'text-zinc-800',
                                                                isEdited && !isFiltered && 'bg-amber-50 hover:bg-amber-100',
                                                                isFiltered && 'bg-zinc-50/50',
                                                            )}
                                                            onClick={() => onCellClick(index, field)}>
                                                            {field.isImage ? (
                                                                <ImageCell value={value} isEdited={isEdited} onHoverEnter={onThumbEnter} onHoverLeave={onThumbLeave} dimmed={isFiltered} onImageClick={(url) => onImageClick(url, (newUrl) => updateCell(index, field.key, newUrl))} />
                                                            ) : value ? (
                                                                <div className="whitespace-pre-wrap break-words leading-relaxed">{value}</div>
                                                            ) : (
                                                                <span className="text-zinc-300">—</span>
                                                            )}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {total > SKU_PAGE_SIZE && (
                        <div className="flex items-center justify-between border-t border-zinc-200/80 bg-zinc-50/50 px-3 py-1.5 text-xs text-zinc-500">
                            <span>第 <span className="font-semibold text-zinc-800">{pageStart + 1}-{pageEnd}</span> 个 SKU / 共 {total}</span>
                            <div className="flex items-center gap-1">
                                <button onClick={() => onPageChange(1)} disabled={safePage <= 1} className="rounded p-1 transition-colors hover:bg-zinc-200 hover:text-zinc-800 disabled:opacity-30"><ChevronsLeft size={14} /></button>
                                <button onClick={() => onPageChange(Math.max(1, safePage - 1))} disabled={safePage <= 1} className="rounded p-1 transition-colors hover:bg-zinc-200 hover:text-zinc-800 disabled:opacity-30"><ChevronLeft size={14} /></button>
                                <span className="min-w-[60px] text-center font-medium text-zinc-700">{safePage} / {totalPages}</span>
                                <button onClick={() => onPageChange(Math.min(totalPages, safePage + 1))} disabled={safePage >= totalPages} className="rounded p-1 transition-colors hover:bg-zinc-200 hover:text-zinc-800 disabled:opacity-30"><ChevronRightIcon size={14} /></button>
                                <button onClick={() => onPageChange(totalPages)} disabled={safePage >= totalPages} className="rounded p-1 transition-colors hover:bg-zinc-200 hover:text-zinc-800 disabled:opacity-30"><ChevronsRight size={14} /></button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// --- 已修改字段 badge（放不下时用 +N 省略号，图片字段显示缩略图，hover 展示前后对比） ---

interface FilteredImageInfo {
    url: string;
    skuName?: string;
    rowIndices: number[];
    fieldKey: 'mainImages' | 'detailImages' | 'skuImage';
}

interface ModifiedBadgeItem {
    key: string;
    label: string;
    isImage: boolean;
    imageUrl?: string;
    originalImageUrl?: string;
    value: string;
    originalValue: string;
    // 低质量过滤项只记录从原数据中实际移除的图片。
    filteredImages?: FilteredImageInfo[];
    onRestoreFiltered?: (images: FilteredImageInfo[]) => void;
    // 当同一字段在多个 SKU 行上有不同修改时（如 SKU 估重），提供逐行前后对比列表
    rows?: { skuName: string; originalValue: string; value: string; imageUrl?: string }[];
}

const RESULT_LABELS: Record<string, string> = {
    firstImage: '首图', productName: 'sku名称', skuSpec1: 'sku名称', skuSpec2: 'sku名称',
    skuWeight: 'sku估重', detailImages: 'ai详情图', skuImage: 'sku图', sizeChart: '尺码图',
};

// 修改项详情内容（同时用于 badge 悬停预览与点击后的大弹窗）
// hover 任意图片时弹出明显放大的全屏预览层（不受弹窗裁剪限制）
function BadgeDetailContent({ it, imageMaxHeight, enableImageZoom = true, onRestored }: {
    it: ModifiedBadgeItem;
    imageMaxHeight?: number;
    enableImageZoom?: boolean;
    onRestored?: () => void;
}) {
    const [zoomSrc, setZoomSrc] = useState<string | null>(null);
    const filteredIdentity = (image: FilteredImageInfo) => `${image.rowIndices.join(',')}\u0000${image.fieldKey}\u0000${image.url}`;
    const [selectedFilteredImages, setSelectedFilteredImages] = useState<Set<string>>(
        () => new Set(),
    );
    useEffect(() => {
        setSelectedFilteredImages(new Set());
    }, [it]);
    const zoomProps = (url?: string) =>
        url && enableImageZoom
            ? {
                onMouseEnter: () => setZoomSrc(url),
                onMouseLeave: () => setZoomSrc(null),
                onClick: () => setZoomSrc(url),
            }
            : {};
    const isSkuNameDetail = it.key === 'skuSpec1' || it.key === 'skuSpec2';

    let body: ReactNode;
    if (it.filteredImages && it.filteredImages.length > 0) {
        const selectedCount = it.filteredImages.filter(image => selectedFilteredImages.has(filteredIdentity(image))).length;
        const restoreSelected = () => {
            if (!it.onRestoreFiltered || selectedCount === 0) return;
            it.onRestoreFiltered(it.filteredImages!.filter(image => selectedFilteredImages.has(filteredIdentity(image))));
            setSelectedFilteredImages(new Set());
            onRestored?.();
        };
        body = (
            <div className="max-h-[64vh] overflow-y-auto rounded-xl bg-[#fafafa] p-4">
                <div className="mb-4 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                        <label className="flex cursor-pointer items-center gap-1.5 text-sm font-medium text-zinc-500 select-none">
                            <input
                                type="checkbox"
                                checked={selectedCount === it.filteredImages.length}
                                onChange={(e) => setSelectedFilteredImages(e.target.checked
                                    ? new Set(it.filteredImages!.map(filteredIdentity))
                                    : new Set())}
                                className="h-4 w-4 accent-[#159b96]"
                            />
                            <span>全选已过滤图片</span>
                        </label>
                        <span className="rounded-md bg-zinc-100 px-2 py-1 text-sm text-zinc-400">共 {it.filteredImages.length} 张</span>
                    </div>
                    <Button variant="outline" size="sm" disabled={selectedCount === 0} onClick={restoreSelected} className="border-[#77cbc7] text-[#159b96]">
                        <RotateCcw size={14} /> 恢复过滤{selectedCount > 0 ? ` (${selectedCount})` : ''}
                    </Button>
                </div>
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
                    {it.filteredImages.map((image, index) => {
                        const identity = filteredIdentity(image);
                        const checked = selectedFilteredImages.has(identity);
                        return (
                        <label key={`${identity}-${index}`} className={cn(
                            'relative flex min-w-0 cursor-pointer flex-col rounded-lg border bg-white p-2 transition-colors',
                            checked ? 'border-[#77cbc7] ring-1 ring-[#b9e7e4]' : 'border-zinc-200',
                        )}>
                            <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => setSelectedFilteredImages(prev => {
                                    const next = new Set(prev);
                                    next.has(identity) ? next.delete(identity) : next.add(identity);
                                    return next;
                                })}
                                className="absolute left-3 top-3 z-10 h-4 w-4 accent-[#159b96]"
                            />
                            <img
                                src={image.url}
                                alt=""
                                {...zoomProps(image.url)}
                                className={cn('aspect-square w-full rounded-md object-contain', enableImageZoom && 'cursor-zoom-in')}
                                onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
                            />
                            {image.skuName && (
                                <p className="mt-2 whitespace-pre-wrap break-words text-xs leading-5 text-zinc-600">{image.skuName}</p>
                            )}
                        </label>
                    )})}
                </div>
            </div>
        );
    } else if (it.isImage) {
        body = (
            <div className="flex items-stretch gap-8">
                <div className="flex min-w-0 flex-1 flex-col rounded-lg">
                    {it.originalImageUrl ? (
                        <img src={it.originalImageUrl} alt="" {...zoomProps(it.originalImageUrl)}
                            style={{ maxHeight: imageMaxHeight ? `${imageMaxHeight}px` : '58vh' }}
                            className={cn('w-full rounded-lg object-contain', enableImageZoom && 'cursor-zoom-in')}
                            onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} />
                    ) : <div className="flex h-40 items-center justify-center rounded-lg border border-dashed border-zinc-200 text-sm text-zinc-300">无</div>}
                    <p className="mt-3 text-center text-base font-semibold text-zinc-800">源数据</p>
                </div>
                <ArrowRight size={28} className="mt-[28%] shrink-0 text-indigo-400" />
                <div className="flex min-w-0 flex-1 flex-col rounded-lg">
                    {it.imageUrl ? (
                        <img src={it.imageUrl} alt="" {...zoomProps(it.imageUrl)}
                            style={{ maxHeight: imageMaxHeight ? `${imageMaxHeight}px` : '58vh' }}
                            className={cn('w-full rounded-lg object-contain', enableImageZoom && 'cursor-zoom-in')}
                            onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} />
                    ) : <div className="flex h-40 items-center justify-center rounded-lg border border-dashed border-zinc-200 text-sm text-zinc-300">无</div>}
                    <p className="mt-3 text-center text-base font-semibold text-[#159b96]">AI处理后</p>
                </div>
            </div>
        );
    } else if (it.rows && it.rows.length > 0 && isSkuNameDetail) {
        body = (
            <div className="max-h-[64vh] overflow-auto rounded-xl bg-[#fafafa] p-4">
                <div className="mb-4 flex items-center gap-3">
                    <h4 className="text-base font-semibold text-zinc-600">{it.label}</h4>
                    <span className="rounded-md bg-zinc-100 px-2 py-1 text-sm text-zinc-400">共 {it.rows.length} 个 SKU 不同</span>
                </div>
                <div className="min-w-[760px]">
                    <div className="grid grid-cols-[minmax(220px,0.8fr)_minmax(260px,1fr)_minmax(260px,1fr)] gap-x-6 border-b border-zinc-200 px-3 pb-3 text-sm font-medium text-zinc-400">
                        <span>SKU</span>
                        <span>原始数据</span>
                        <span>修改后</span>
                    </div>
                    <div className="divide-y divide-zinc-100">
                        {it.rows.map((r, i) => (
                            <div key={i} className="grid min-h-[70px] grid-cols-[minmax(220px,0.8fr)_minmax(260px,1fr)_minmax(260px,1fr)] items-center gap-x-6 px-3 py-2.5 text-sm">
                                <div className="flex min-w-0 items-center gap-3">
                                    {r.imageUrl ? (
                                        <img src={r.imageUrl} alt="" className="h-12 w-12 shrink-0 rounded-md object-contain bg-white" onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} />
                                    ) : (
                                        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md border border-dashed border-zinc-200 text-[10px] text-zinc-300">无图</div>
                                    )}
                                    <span className="whitespace-pre-wrap break-words leading-5 text-zinc-600">{r.skuName}</span>
                                </div>
                                <span className="whitespace-pre-wrap break-words leading-5 text-red-600 line-through decoration-red-400">{r.originalValue || '（空）'}</span>
                                <span className="whitespace-pre-wrap break-words text-base font-medium leading-6 text-emerald-700">{r.value || '（空）'}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    } else if (it.rows && it.rows.length > 0) {
        body = (
            <div className="grid max-h-[60vh] grid-cols-1 gap-5 overflow-y-auto pr-2 md:grid-cols-2 xl:grid-cols-3">
                <p className="col-span-full text-sm font-medium text-zinc-400">共 {it.rows.length} 个 SKU 处理</p>
                {it.rows.map((r, i) => (
                    <div key={i} className="flex min-h-[142px] gap-4 rounded-xl bg-[#f6f6f6] p-4">
                        {r.imageUrl ? (
                            <img src={r.imageUrl} alt="" {...zoomProps(r.imageUrl)}
                                className="h-28 w-28 shrink-0 self-center cursor-zoom-in rounded-lg object-contain bg-white"
                                onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} />
                        ) : (
                            <div className="flex h-28 w-28 shrink-0 items-center justify-center self-center border border-dashed border-zinc-200 text-xs text-zinc-300">无图</div>
                        )}
                        <div className="flex min-w-0 flex-1 flex-col justify-center">
                            <p className="mb-4 whitespace-pre-wrap break-words text-sm font-semibold leading-5 text-zinc-800" title={r.skuName}>{r.skuName}</p>
                            <div className="grid w-full grid-cols-[44px_minmax(0,1fr)] items-center gap-y-3 text-sm">
                                <span className="text-zinc-400">原值</span>
                                <span className="min-w-0 whitespace-pre-wrap break-words bg-zinc-100 px-2 py-1 text-center tabular-nums text-zinc-400">{r.originalValue || '（空）'}</span>
                                <span className="font-semibold text-[#159b96]">修改后</span>
                                <span className="min-w-0 whitespace-pre-wrap break-words bg-[#ebfaf8] px-2 py-1 text-center font-semibold tabular-nums text-[#159b96]">{r.value || '（空）'}</span>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        );
    } else {
        body = (
            <div className="space-y-2">
                <div>
                    <p className="mb-0.5 text-xs font-medium uppercase tracking-wider text-zinc-400">原值</p>
                    <p className="whitespace-pre-wrap break-words rounded-lg bg-zinc-50 px-3 py-2 text-sm text-zinc-500 line-through decoration-zinc-300">{it.originalValue || '（空）'}</p>
                </div>
                <div>
                    <p className="mb-0.5 text-xs font-medium uppercase tracking-wider text-indigo-400">修改后</p>
                    <p className="whitespace-pre-wrap break-words rounded-lg bg-indigo-50 px-3 py-2 text-sm font-medium text-indigo-700">{it.value || '（空）'}</p>
                </div>
            </div>
        );
    }

    return (
        <>
            {body}
            {zoomSrc && createPortal(
                <div className="pointer-events-none fixed inset-0 z-[200] flex items-center justify-center p-6">
                    <img
                        src={zoomSrc}
                        alt=""
                        className="max-h-[90vh] max-w-[90vw] rounded-lg object-contain shadow-2xl ring-1 ring-black/10"
                    />
                </div>,
                document.body,
            )}
        </>
    );
}

function ModifiedFieldBadges({ items }: { items: ModifiedBadgeItem[] }) {
    const scrollRef = useRef<HTMLDivElement>(null);
    const [hoverItem, setHoverItem] = useState<ModifiedBadgeItem | null>(null);
    const [hoverPos, setHoverPos] = useState<{ left: number; top: number } | null>(null);
    const [detailItem, setDetailItem] = useState<ModifiedBadgeItem | null>(null);
    const hoverCloseRef = useRef<number | null>(null);

    if (items.length === 0) return null;

    const handleEnter = (e: React.MouseEvent<HTMLElement>, it: ModifiedBadgeItem) => {
        if (hoverCloseRef.current !== null) window.clearTimeout(hoverCloseRef.current);
        const rect = e.currentTarget.getBoundingClientRect();
        const popW = Math.min(1250, window.innerWidth - 32);
        const left = Math.min(Math.max(16, rect.left - 160), window.innerWidth - popW - 16);
        setHoverPos({ left: Math.max(16, left), top: rect.bottom + 8 });
        setHoverItem(it);
    };
    const keepHoverOpen = () => {
        if (hoverCloseRef.current !== null) window.clearTimeout(hoverCloseRef.current);
    };
    const handleLeave = () => {
        hoverCloseRef.current = window.setTimeout(() => {
            setHoverItem(null);
            setHoverPos(null);
        }, 120);
    };
    const displayLabel = (it: ModifiedBadgeItem) => it.label.startsWith('过滤') ? it.label : (RESULT_LABELS[it.key] ?? it.label);
    const previewImage = (it: ModifiedBadgeItem) => it.imageUrl || it.filteredImages?.[0]?.url || it.rows?.find(row => row.imageUrl)?.imageUrl;
    const filterItems = items.filter(item => item.key.startsWith('filter-'));
    const regularItems = items.filter(item => !item.key.startsWith('filter-'));
    const renderBadge = (it: ModifiedBadgeItem) => {
        const imageUrl = previewImage(it);
        return (
            <button key={it.key} type="button" data-badge
                onMouseEnter={(e) => handleEnter(e, it)}
                onMouseLeave={handleLeave}
                onClick={(e) => { e.stopPropagation(); setDetailItem(it); }}
                className={cn(
                    'flex shrink-0 cursor-pointer flex-col overflow-hidden border border-[#77cbc7] bg-[#f2fbfa] text-[#159b96] shadow-sm transition-colors hover:bg-[#e3f7f5] hover:shadow-md',
                    'h-[84px] w-[72px] rounded-lg',
                )}>
                <span className="flex h-[64px] w-full items-center justify-center overflow-hidden rounded-t-[7px] bg-white p-0.5">
                    {imageUrl ? (
                        <img src={imageUrl} alt="" className="h-full w-full rounded-md object-contain"
                            onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} />
                    ) : (
                        <span className="max-w-full truncate px-1 text-xs font-semibold text-[#159b96]">{it.value || '已处理'}</span>
                    )}
                </span>
                <span className="flex min-h-0 flex-1 items-center justify-center px-0.5 text-[11px] font-semibold leading-none">
                    {displayLabel(it)}
                </span>
            </button>
        );
    };

    return (
        <>
            <div ref={scrollRef} className="flex w-full min-w-0 items-end justify-start gap-3 overflow-x-auto overflow-y-hidden pb-1">
                {regularItems.map(renderBadge)}
                {filterItems.length > 0 && (
                    <div className="flex shrink-0 flex-col gap-1">
                        <span className="text-[10px] font-semibold leading-3 text-[#6c7a80]">过滤低质量</span>
                        <div className="flex gap-3 border-l-2 border-[#8dd4d1] pl-2">
                            {filterItems.map(renderBadge)}
                        </div>
                    </div>
                )}
            </div>

            {hoverItem && hoverPos && createPortal(
                <div className="fixed z-50 w-[calc(100vw-32px)] max-w-[1250px] rounded-2xl border border-[#8dd4d1] bg-white p-8 shadow-2xl"
                    style={{
                        left: hoverPos.left,
                        top: hoverPos.top,
                        maxHeight: `calc(100vh - ${hoverPos.top + 16}px)`,
                        overflowY: hoverItem.isImage ? 'hidden' : 'auto',
                    }}
                    onMouseEnter={keepHoverOpen} onMouseLeave={handleLeave}>
                    <div className="mb-6 flex items-center border-b border-zinc-100 pb-4">
                        <h3 className="text-xl font-semibold text-zinc-900">
                            {displayLabel(hoverItem)}{hoverItem.key.startsWith('filter-') ? ' · 修改详情' : ''}
                        </h3>
                        <span className="ml-auto text-sm text-indigo-400">点击查看大图</span>
                    </div>
                    <BadgeDetailContent
                        it={hoverItem}
                        imageMaxHeight={Math.max(120, window.innerHeight - hoverPos.top - 210)}
                        enableImageZoom={false}
                        onRestored={() => { setHoverItem(null); setHoverPos(null); }}
                    />
                </div>,
                document.body
            )}

            <Modal open={!!detailItem} onClose={() => setDetailItem(null)} title={detailItem ? `${displayLabel(detailItem)} · 修改详情` : ''} width="xl">
                {detailItem && <BadgeDetailContent it={detailItem} onRestored={() => setDetailItem(null)} />}
            </Modal>
        </>
    );
}

// --- 内联图片（无数量限制） ---

function InlineImageCell({ value, onHoverEnter, onHoverLeave, onImageClick }: { value: string; onHoverEnter: (e: React.MouseEvent, url: string) => void; onHoverLeave: () => void; onImageClick?: (url: string) => void }) {
    const [errored, setErrored] = useState<Set<string>>(new Set());
    const urls = parseImageUrls(value);
    if (urls.length === 0) return <span className="text-zinc-300">—</span>;
    return (
        <div className="flex flex-wrap gap-1">
            {urls.map((url, i) => (
                errored.has(url) ? (
                    <div key={`${url}-${i}`} className="flex h-8 w-8 items-center justify-center rounded border border-zinc-200 bg-zinc-50"><ImageOff size={12} className="text-zinc-300" /></div>
                ) : (
                    <img key={`${url}-${i}`} src={url} alt="" className="h-8 w-8 rounded border border-zinc-200 object-cover transition-transform hover:scale-150"
                        onError={() => setErrored(prev => new Set(prev).add(url))}
                        onMouseEnter={(e) => onHoverEnter(e, url)} onMouseLeave={onHoverLeave}
                        onClick={(e) => { e.stopPropagation(); onImageClick?.(url); }} />
                )
            ))}
        </div>
    );
}

// --- 图片单元格（无数量限制） ---

function ImageCell({ value, isEdited, onHoverEnter, onHoverLeave, dimmed, onImageClick }: { value: string; isEdited: boolean; onHoverEnter: (e: React.MouseEvent, url: string) => void; onHoverLeave: () => void; dimmed?: boolean; onImageClick?: (url: string) => void }) {
    const [errored, setErrored] = useState<Set<string>>(new Set());
    const urls = parseImageUrls(value);
    if (urls.length === 0) return <span className="text-zinc-300">—</span>;
    return (
        <div className={cn("flex flex-wrap gap-1", dimmed && "opacity-40")}>
            {isEdited && !dimmed && <span className="mb-0.5 inline-block rounded bg-amber-200 px-1 text-[10px] font-medium text-amber-800">已改</span>}
            {dimmed && <span className="mb-0.5 inline-block rounded bg-zinc-200 px-1 text-[10px] font-medium text-zinc-500">已过滤</span>}
            {urls.map((url, i) => (
                <div key={`${url}-${i}`} className="relative">
                    {errored.has(url) ? (
                        <div className="flex h-9 w-9 items-center justify-center rounded border border-zinc-200 bg-zinc-50"><ImageOff size={14} className="text-zinc-300" /></div>
                    ) : (
                        <img src={url} alt="" className="h-9 w-9 cursor-pointer rounded border border-zinc-200 object-cover transition-transform hover:scale-150"
                            onError={() => setErrored(prev => new Set(prev).add(url))}
                            onMouseEnter={(e) => onHoverEnter(e, url)} onMouseLeave={onHoverLeave}
                            onClick={(e) => { e.stopPropagation(); onImageClick?.(url); }} />
                    )}
                </div>
            ))}
        </div>
    );
}
